import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import SectionExport, { SectionExportContext } from "@/components/SectionExport";
import { api } from "@/lib/api";
import { containerServings, packageMass } from "@/lib/serving-math";
import { validateRecipeSave, recipeSaveError } from "@/lib/recipe-save";
import useFormulaDraft from "@/lib/useFormulaDraft";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NutritionLabel from "@/components/NutritionLabel";
import GuaranteedAnalysisLabel from "@/components/GuaranteedAnalysisLabel";
import PetSafetyPanel from "@/components/PetSafetyPanel";
import AIAssistant from "@/components/AIAssistant";
import ClaimsPanel from "@/components/ClaimsPanel";
import ApplicationCalculators from "@/components/ApplicationCalculators";
import LabelingIntelligencePanel from "@/components/LabelingIntelligencePanel";
import AcidifiedFoodsChecklist from "@/components/AcidifiedFoodsChecklist";
import SafetyPlanPanel from "@/components/SafetyPlanPanel";
import RecipeRowLotPicker from "@/components/RecipeRowLotPicker";
import FlavorRadar from "@/components/FlavorRadar";
import { computeLabelReadiness, readinessStatus } from "@/lib/readiness";
import { PRODUCT_TYPES, calculateAverageDensity, usesLiquidMeasurements } from "@/lib/product-types";
import { Plus, Trash, FloppyDisk, Printer, MagnifyingGlass, Scales, Warning, CopySimple, Info, Calculator, ChefHat, Storefront, Files, ShieldCheck, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

// How long to keep the print iframe in the DOM before giving up on `afterprint`
// cleanup (e.g., user cancels the print dialog on browsers that don't fire it).
const PRINT_IFRAME_CLEANUP_MS = 60_000;

// Container-size presets for printable labels. Wrap presets use the FDA
// linear (small-package) nutrition format permitted by 21 CFR 101.9(j)(13)
// for packages with limited label space — standard practice on spice bottles.
// Escape user-entered text before interpolating into print-iframe HTML.
const esc = (s) => String(s ?? "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
// Only allow safe base64 image data URIs as logo sources.
const SAFE_LOGO_RE = /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/;

const LABEL_PRESETS = {
  sheet: { name: "Full sheet (vertical FDA panel)", wrap: false },
  spice2: { name: "2 oz spice bottle wrap — 4\" × 2\"", wrap: true, w: 4, h: 2, fs: 1 },
  spice4: { name: "4 oz spice bottle wrap — 5\" × 2.25\"", wrap: true, w: 5, h: 2.25, fs: 1 },
  spice6: { name: "6 oz spice bottle wrap — 5.5\" × 2.5\"", wrap: true, w: 5.5, h: 2.5, fs: 1.1 },
  jar4: { name: "4 oz wide-neck jar wrap — 6.5\" × 2\"", wrap: true, w: 6.5, h: 2, fs: 1 },
  jar8: { name: "8 oz jar wrap — 7\" × 2.5\"", wrap: true, w: 7, h: 2.5, fs: 1.15 },
  sample: { name: "Sample bag — 4\" × 3\"", wrap: true, w: 4, h: 3, fs: 1 },
  pouch: { name: "Stand-up pouch back panel — 4\" × 6\"", wrap: false, w: 4, h: 6 },
  tub: { name: "Foodservice tub — 6\" × 4\"", wrap: true, w: 6, h: 4, fs: 1.4 },
  gallon: { name: "Gallon jug — 8\" × 4\"", wrap: true, w: 8, h: 4, fs: 1.6 },
};

// Label purpose — stamps non-commercial labels so prototypes and samples can't
// be mistaken for retail product. Commercial prints clean (no stamp).
const LABEL_PURPOSES = {
  commercial: { name: "Retail / Commercial Production", icon: "🛒", stamp: "" },
  prototype: { name: "Prototype", icon: "🧫", stamp: "PROTOTYPE — NOT FOR SALE" },
  customer_sample: { name: "Customer Sample", icon: "👥", stamp: "CUSTOMER SAMPLE — NOT FOR RESALE" },
  pilot: { name: "Pilot Production", icon: "🏭", stamp: "PILOT PRODUCTION" },
  rnd: { name: "Internal R&D", icon: "🧪", stamp: "INTERNAL R&D — NOT FOR SALE" },
};

// Label readiness — scoring logic lives in @/lib/readiness (shared with the
// Commercialization Center hub, which surfaces the same score as its headline).

// ----- Courtney's Insights — contextual expert warnings ---------------------
// Translates the backend's claim validation (claim_validator.py) and
// formulation guard-rails (formulation_intelligence.py) into plain-language,
// advisory insight cards in Courtney's voice. Advisory only — draft-for-QA
// framing, never a compliance guarantee.
function buildCourtneysInsights({ calc, productName, recipeName, mode, ph }) {
  if (!calc) return [];
  const insights = [];
  const nameText = `${productName || ""} ${recipeName || ""}`.toLowerCase();

  // 1. Claims the label wording implies but the formula doesn't support.
  //    Match each FDA claim phrase against the product/recipe name; keep only
  //    the longest phrase when one contains another ("Very Low Sodium" vs
  //    "Low Sodium") so we don't double-flag.
  const claims = Array.isArray(calc.claims) ? calc.claims : [];
  const mentioned = claims
    .filter((c) => nameText.includes(c.claim.toLowerCase()))
    .sort((a, b) => b.claim.length - a.claim.length)
    .filter((c, _, arr) => !arr.some((o) => o !== c && o.claim.toLowerCase().includes(c.claim.toLowerCase()) && nameText.includes(o.claim.toLowerCase())));
  for (const c of mentioned) {
    if (c.allowed) {
      insights.push({
        tone: "good",
        text: `Your product name suggests a "${c.claim}" claim, and the formula currently supports it (${c.actual} vs ${c.threshold}, ${c.basis}). Keep documentation on file and have your QA reviewer confirm before printing.`,
      });
    } else {
      insights.push({
        tone: "warning",
        text: `This product exceeds the FDA threshold for a "${c.claim}" claim (${c.actual} vs ${c.threshold}, ${c.basis}). Remove the claim from the label wording or adjust the formula — then re-check with your QA reviewer.`,
      });
    }
  }

  // 2. Formulation guard-rails for the active mode, rephrased as advice.
  const warnings = calc.formulation_warnings?.[mode] || [];
  for (const w of warnings) {
    if (w.level === "info") {
      insights.push({
        tone: "info",
        text: `${w.ingredient} is at ${w.pct}% — below the typical ${mode} minimum of ${w.limit}%. That can be intentional, but taste it against a benchmark before committing. ${w.reason}.`,
      });
    } else {
      insights.push({
        tone: w.level === "warning" ? "warning" : "caution",
        text: `${w.ingredient} exceeds typical ${mode} usage (${w.pct}% vs ~${w.limit}% max). ${w.reason}. Review this before commercialization — high inclusions like this usually show up in the cup before they show up on the label.`,
      });
    }
  }

  // 3. Acidity / pH cross-check — if the formula carries a notable food-acid
  //    load but no target pH is recorded, suggest reviewing target pH.
  //    Advisory only; pH decisions belong with a process authority.
  const acidity = calc.acidity_check;
  if (acidity?.notable && !ph) {
    const names = (acidity.acid_ingredients || []).map((a) => `${a.name} (${a.pct}%)`).join(", ");
    insights.push({
      tone: "caution",
      text: `This formula carries a notable acid load — ${names} — but no target pH is entered. Acids this prominent usually mean you're managing pH on purpose. Record your target pH in the Safety Plan inputs and review it with your process authority before commercialization.`,
    });
  }

  return insights;
}

function CourtneysInsights({ calc, productName, recipeName, mode, ph }) {
  const insights = buildCourtneysInsights({ calc, productName, recipeName, mode, ph });
  if (insights.length === 0) return null;
  const toneCls = {
    warning: "bg-red-50 border-red-200 text-red-900",
    caution: "bg-amber-50 border-amber-200 text-amber-900",
    info: "bg-blue-50 border-blue-200 text-blue-900",
    good: "bg-emerald-50 border-emerald-200 text-emerald-900",
  };
  return (
    <div className="mt-2 space-y-1.5" data-testid="courtneys-insights">
      {insights.map((ins, i) => (
        <div key={i} className={`rounded-md border px-2.5 py-2 ${toneCls[ins.tone] || toneCls.caution}`}
          data-testid={`courtneys-insight-${i}`}>
          <div className="text-[9px] uppercase tracking-[0.15em] font-bold opacity-80 mb-0.5">
            ⚠ Courtney's Insight
          </div>
          <div className="text-[10px] leading-snug">{ins.text}</div>
        </div>
      ))}
      <div className="text-[8px] text-stone-400 leading-tight">
        Insights are formulation guidance for your draft, based on published FDA thresholds and
        industry usage norms — not a compliance review. Final sign-off belongs to your QA reviewer.
      </div>
    </div>
  );
}

function LabelReadiness({ calc, bp, netWeight, productName, recipeName, mode, ph }) {
  if (!calc) return null;
  const { checks, score } = computeLabelReadiness({ calc, bp, netWeight, productName });
  const mark = (s) => s === "pass" ? "✓" : s === "warn" ? "⚠" : "✗";
  const color = (s) => s === "pass" ? "text-emerald-700" : s === "warn" ? "text-amber-600" : "text-red-700";
  const status = readinessStatus(score);
  return (
    <div className="border border-stone-200 rounded-md p-3 bg-white" data-testid="label-readiness">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.15em] text-stone-500 font-semibold">Commercial Readiness Check</div>
        <div className={`text-xs font-bold ${status.cls}`} data-testid="label-readiness-score">
          {status.dot} {status.text} <span className="font-normal text-stone-400">({score}%)</span>
        </div>
      </div>
      <div className="space-y-1">
        {checks.map((c, i) => (
          <div key={i} className="flex items-start gap-1.5 text-[10px] leading-tight">
            <span className={`font-bold ${color(c.status)}`}>{mark(c.status)}</span>
            <span>
              <span className={c.status === "fail" ? "text-red-700" : "text-stone-700"}>{c.label}</span>
              {c.note && <span className="text-stone-400"> — {c.note}</span>}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 text-[8px] text-stone-400 leading-tight">
        Checks that common required label elements are present. Not a legal compliance review.
      </div>
      <CourtneysInsights calc={calc} productName={productName} recipeName={recipeName} mode={mode} ph={ph} />
    </div>
  );
}

// Application Calculator filtering — maps intended-use category → protein keys
// shown in the dropdown. "all" means no filter.
const PROTEIN_CATEGORIES = {
  meat_seafood: ["chicken", "beef", "lamb", "pork", "fish", "shrimp"],
  plant:        ["vegetables", "tofu", "rice_grains"],
  snacks:       ["popcorn", "chips_snacks"],
  liquid:       ["soup_broth"],
};

// Industry-average production costs per 4 oz spice jar (US 2026, hand-fill).
// Used as one-click defaults; user can adjust each independently.
const INDUSTRY_DEFAULT_COSTS = {
  packaging_per_unit: 0.85,  // 4 oz amber jar + shaker fitment + cap + label
  labor_per_unit:     0.50,  // ~$18/hr × 30 jars/hr (fill, cap, label by hand)
  copacker_per_unit:  0.00,  // most founders start in-house; co-packer typically $0.50–1.50/unit
  overhead_per_unit:  0.20,  // kitchen rent / utilities / misc allocated per unit
};

export default function RecipeBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = user?.role === "demo" || user?.is_demo;
  const bp = user?.business_profile || {};
  const [name, setName] = useState("");
  const [loaded, setLoaded] = useState(!id);
  const [loadError, setLoadError] = useState("");
  const [savedStamp, setSavedStamp] = useState(null);
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [productName, setProductName] = useState("");
  const [sku, setSku] = useState("");
  const [version, setVersion] = useState("v1");
  const [status, setStatus] = useState("R&D");
  const [parentRecipeId, setParentRecipeId] = useState(null);
  const [clonedFromStarter, setClonedFromStarter] = useState(null);
  const [netWeight, setNetWeight] = useState("4 oz (113g)");
  const [servingSize, setServingSize] = useState("");
  const [servingMeasure, setServingMeasure] = useState("");
  const [servingsPer, setServingsPer] = useState(1);
  const [rows, setRows] = useState([]); // [{ingredient_id, grams, _display, _price}]
  const [batchSizeG, setBatchSizeG] = useState(1000); // target batch size in grams
  const [search, setSearch] = useState("");
  const [found, setFound] = useState([]);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [markupPct, setMarkupPct] = useState(300); // default 3x markup (food CPG standard ~3-4x)
  const [unitsPerBatch, setUnitsPerBatch] = useState(10); // how many retail units from this batch
  // Pack-out — defines what a "unit" actually is (e.g., "4 oz jar")
  const [unitLabel, setUnitLabel] = useState(""); // freeform: "4 oz jar", "8 oz pouch"
  const [unitSizeG, setUnitSizeG] = useState(""); // net weight per unit, grams
  // Fully-loaded cost inputs (per retail unit) — packaging, labor, co-packer fee, other overhead
  const [packagingPerUnit, setPackagingPerUnit] = useState(0);
  const [laborPerUnit, setLaborPerUnit] = useState(0);
  const [copackerPerUnit, setCopackerPerUnit] = useState(0);
  const [overheadPerUnit, setOverheadPerUnit] = useState(0);
  const [retailOverride, setRetailOverride] = useState(""); // optional explicit retail price
  // Application Calculator category filter — meat_seafood | plant | snacks | liquid | all
  const [intendedUseCategory, setIntendedUseCategory] = useState("meat_seafood");
  const [mode, setMode] = useState("retail"); // retail | chef — drives auto-flag thresholds
  const [notes, setNotes] = useState("");
  const [productType, setProductType] = useState("dry_blend");
  const [productForm, setProductForm] = useState("");
  // Pet Toppers mode (Pawspice). species drives the AAFCO Guaranteed Analysis;
  // ashPct is an optional measured lab value for the calorie (ME) math.
  const [species, setSpecies] = useState("dog"); // dog | cat
  const [ashPct, setAshPct] = useState("");
  const [productionMethod, setProductionMethod] = useState("");
  const [labProductionMethod, setLabProductionMethod] = useState("");
  const [labBenchProcedure, setLabBenchProcedure] = useState("");
  // Safety-plan structured inputs (feed the DRAFT hazard-analysis worksheet)
  const [ph, setPh] = useState("");
  const [waterActivity, setWaterActivity] = useState("");
  const [thermalTempF, setThermalTempF] = useState("");
  const [thermalHoldMin, setThermalHoldMin] = useState("");
  const [acidified, setAcidified] = useState(false);
  const [killStep, setKillStep] = useState("");
  const [distributionMode, setDistributionMode] = useState("");
  const [safetyPlan, setSafetyPlan] = useState(null);
  const [showLabProcedure, setShowLabProcedure] = useState(false);
  const [showLabProduction, setShowLabProduction] = useState(false);
  const [showProductionMethod, setShowProductionMethod] = useState(false);
  const [showSpecSheet, setShowSpecSheet] = useState(false);
  const [showApplicationCalcs, setShowApplicationCalcs] = useState(true);
  const [declaredSoi, setDeclaredSoi] = useState(null);
  const [proteins, setProteins] = useState([]);
  const [appProtein, setAppProtein] = useState("chicken");
  const [appWeightLb, setAppWeightLb] = useState(5);
  const [appIntensity, setAppIntensity] = useState("medium");
  const [appResult, setAppResult] = useState(null);

  const [intelDrawer, setIntelDrawer] = useState(null); // {ingredient, intel}
  const [labelPreset, setLabelPreset] = useState("sheet"); // print size preset (LABEL_PRESETS key)
  const [labelPurpose, setLabelPurpose] = useState("commercial"); // LABEL_PURPOSES key
  const [fixSuggestions, setFixSuggestions] = useState({}); // { warningKey: { loading, text } }
  // Spec sheet fields
  const [spec, setSpec] = useState({
    description: "", aroma_profile: "", flavor_profile: "", color: "",
    mesh_size: "", bulk_density_g_ml: "", moisture_max_pct: "",
    shelf_life_months: "", storage_conditions: "", packaging: "",
    micro_spec: "", application_notes: "",
  });
  // Flavor Architecture — sensory profile (0–5) shown on the dashboard card
  const [flavorArch, setFlavorArch] = useState({ front: 0, body: 0, finish: 0, thermal: 0 });

  // Load supported proteins for Application Calculator
  const draftValues = { requestId, name, productName, sku, version, status, parentRecipeId, netWeight,
    servingSize, servingMeasure, servingsPer, rows, mode, notes, productType, productForm,
    species, ashPct, productionMethod, labProductionMethod, labBenchProcedure, declaredSoi,
    ph, waterActivity, thermalTempF, thermalHoldMin, acidified, killStep, distributionMode,
    spec, flavorArch, unitLabel, unitSizeG, packagingPerUnit, laborPerUnit, copackerPerUnit,
    overheadPerUnit, markupPct, retailOverride, intendedUseCategory };
  const draftSetters = { requestId:setRequestId, name:setName, productName:setProductName, sku:setSku, version:setVersion,
    status:setStatus, parentRecipeId:setParentRecipeId, netWeight:setNetWeight, servingSize:setServingSize,
    servingMeasure:setServingMeasure, servingsPer:setServingsPer, rows:setRows, mode:setMode,
    notes:setNotes, productType:setProductType, productForm:setProductForm, species:setSpecies,
    ashPct:setAshPct, productionMethod:setProductionMethod, labProductionMethod:setLabProductionMethod,
    labBenchProcedure:setLabBenchProcedure, declaredSoi:setDeclaredSoi, ph:setPh, waterActivity:setWaterActivity,
    thermalTempF:setThermalTempF, thermalHoldMin:setThermalHoldMin, acidified:setAcidified,
    killStep:setKillStep, distributionMode:setDistributionMode, spec:setSpec, flavorArch:setFlavorArch,
    unitLabel:setUnitLabel, unitSizeG:setUnitSizeG, packagingPerUnit:setPackagingPerUnit,
    laborPerUnit:setLaborPerUnit, copackerPerUnit:setCopackerPerUnit, overheadPerUnit:setOverheadPerUnit,
    markupPct:setMarkupPct, retailOverride:setRetailOverride, intendedUseCategory:setIntendedUseCategory };
  const draft = useFormulaDraft({ userId: isDemo ? null : user?.id, recipeId:id, ready:loaded,
    values:draftValues, stamp:savedStamp, restore:(values,stamp) => {
      Object.entries(values).forEach(([key,value]) => draftSetters[key]?.(value));
      setSavedStamp(stamp);
    } });
  useEffect(() => {
    api.get("/application/proteins").then((r) => setProteins(r.data.proteins || [])).catch(() => {});
  }, []);

  // Auto-recalc application when inputs change OR formula's sodium changes
  useEffect(() => {
    if (!appProtein || !appWeightLb || !appIntensity) { setAppResult(null); return; }
    const baseG = Number(appWeightLb) * 453.592;
    // nutrition.raw is PER SERVING; convert to a true per-100g density before
    // feeding the application calculator (otherwise sodium guidance is wrong by
    // a factor of 100/serving_size_g — e.g. 20x low for a 5g serving).
    const servG = Number(calc?.serving_size_g) || Number(servingSize) || 0;
    const rawNa = calc?.nutrition?.raw?.sodium_mg;
    const sodiumPer100g = (rawNa != null && servG > 0) ? (rawNa * 100 / servG) : null;
    const t = setTimeout(() => {
      api.post("/application/calculate", {
        protein: appProtein, base_weight_g: baseG, intensity: appIntensity,
        blend_sodium_mg_per_100g: sodiumPer100g,
      }).then((r) => setAppResult(r.data)).catch(() => setAppResult(null));
    }, 250);
    return () => clearTimeout(t);
  }, [appProtein, appWeightLb, appIntensity, calc]);

  // Auto-derive servings per container from net unit weight / serving size,
  // applying the FDA rounding rule (21 CFR 101.9(b)(8)): a count of 2-5 rounds
  // to the nearest 0.5, otherwise to the nearest whole number. e.g. a 113g jar
  // at a 5g serving = 22.6 -> "about 23", not the hand-entered default of 1.
  const effectiveUnitGrams = Number(unitSizeG) > 0 ? Number(unitSizeG) : packageMass(netWeight);
  useEffect(() => {
    const result = containerServings(effectiveUnitGrams, servingSize);
    if (result) setServingsPer(result.value);
  }, [effectiveUnitGrams, servingSize]);
  const servingCount = containerServings(effectiveUnitGrams, servingSize);
  const servingCountDisplay = servingCount?.display || "Serving count unavailable — enter package and serving weights";
  const servingSizeDisplay = servingMeasure.trim()
    ? `${servingMeasure.trim()} (${servingSize} g)` : `${servingSize} g`;

  const openIntel = async (ingredientId, name) => {
    try {
      const { data } = await api.get(`/ingredients/${ingredientId}/intel`);
      setIntelDrawer({ name, ...data });
    } catch (err) { console.error("intel fetch failed", err); toast.error("Could not load intel"); }
  };

  const requestFix = async (warning) => {
    const key = `${warning.ingredient}-${warning.pct}`;
    if (fixSuggestions[key]?.text || fixSuggestions[key]?.loading) return;
    setFixSuggestions((s) => ({ ...s, [key]: { loading: true } }));
    try {
      const formula = rows.map((r) => ({
        name: r._display,
        grams: Number(r.grams) || 0,
        pct: totalG ? (Number(r.grams) / totalG * 100) : 0,
      }));
      const { data } = await api.post("/formulation/suggest-fix", {
        formula,
        flagged_ingredient: warning.ingredient,
        current_pct: warning.pct,
        typical_max: warning.limit,
        reason: warning.reason,
        mode,
      });
      setFixSuggestions((s) => ({ ...s, [key]: { loading: false, text: data.suggestion } }));
    } catch (err) {
      console.error("Suggest fix failed", err);
      const detail = err?.response?.data?.detail;
      if (detail?.error === "demo_no_access") {
        toast.info("AI suggest-fix is not available in the demo. Sign in to use it.");
      } else {
        toast.error("Could not generate suggestion — try the AI Assistant directly");
      }
      setFixSuggestions((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  // Load recipe if editing
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoaded(false); setLoadError("");
    api.get(`/recipes/${id}`).then(async ({ data }) => {
      if (cancelled) return;
      setSavedStamp(data.updated_at || "");
      setName(data.name); setProductName(data.product_name || "");
      setServingSize(data.serving_size_g); setServingsPer(data.servings_per_container);
      setServingMeasure(data.serving_measure || "");
      setNetWeight(data.net_weight || (data.unit_size_g > 0 ? `${data.unit_size_g} g` : "4 oz (113g)"));
      setSku(data.sku || ""); setVersion(data.version || "v1");
      setStatus(data.status || "R&D"); setParentRecipeId(data.parent_recipe_id || null);
      setClonedFromStarter(data.cloned_from_starter || null);
      setMode(data.mode || "retail");
      setNotes(data.notes || "");
      setProductType(data.product_type || "dry_blend");
      setProductForm(data.product_form || "");
      setSpecies(data.species || "dog");
      setAshPct(data.ash_pct ?? "");
      setProductionMethod(data.production_method || "");
      setLabProductionMethod(data.lab_production_method || "");
      setLabBenchProcedure(data.lab_bench_procedure || "");
      setDeclaredSoi(data.declared_soi || null);
      setPh(data.ph ?? "");
      setWaterActivity(data.water_activity ?? "");
      setThermalTempF(data.thermal_temp_f ?? "");
      setThermalHoldMin(data.thermal_hold_min ?? "");
      setAcidified(!!data.acidified);
      setKillStep(data.kill_step ?? "");
      setDistributionMode(data.distribution_mode ?? "");
      setSafetyPlan(data.safety_plan || null);
      setSpec({
        description: data.description || "",
        aroma_profile: data.aroma_profile || "",
        flavor_profile: data.flavor_profile || "",
        color: data.color || "",
        mesh_size: data.mesh_size || "",
        bulk_density_g_ml: data.bulk_density_g_ml ?? "",
        moisture_max_pct: data.moisture_max_pct ?? "",
        shelf_life_months: data.shelf_life_months ?? "",
        storage_conditions: data.storage_conditions || "",
        packaging: data.packaging || "",
        micro_spec: data.micro_spec || "",
        application_notes: data.application_notes || "",
      });
      setFlavorArch({
        front: data.flavor_architecture?.front ?? 0,
        body: data.flavor_architecture?.body ?? 0,
        finish: data.flavor_architecture?.finish ?? 0,
        thermal: data.flavor_architecture?.thermal ?? 0,
      });
      // Hydrate cost economics
      setUnitLabel(data.unit_label || "");
      setUnitSizeG(data.unit_size_g ?? "");
      setPackagingPerUnit(data.packaging_cost_per_unit ?? 0);
      setLaborPerUnit(data.labor_cost_per_unit ?? 0);
      setCopackerPerUnit(data.copacker_cost_per_unit ?? 0);
      setOverheadPerUnit(data.overhead_cost_per_unit ?? 0);
      setMarkupPct(data.markup_pct ?? 300);
      setRetailOverride(data.retail_price_override ?? "");
      setIntendedUseCategory(data.intended_use_category || "meat_seafood");
      // hydrate display names + prices
      const ings = await api.get("/ingredients");
      if (cancelled) return;
      const byId = Object.fromEntries(ings.data.map((x) => [x.id, x]));
      setRows(data.ingredients.map((r) => ({
        ...r,
        _display: byId[r.ingredient_id]?.name || r.ingredient_id,
        _price: Number(byId[r.ingredient_id]?.price_per_lb || 0),
        _is_liquid: !!byId[r.ingredient_id]?.is_liquid,
        _density: Number(byId[r.ingredient_id]?.density_g_ml || 0),
        _role: byId[r.ingredient_id]?.functional_role || "",
      })));
      setLoaded(true);
    }).catch(() => { if (!cancelled) setLoadError("Could not load the complete saved formula. Saving is disabled to protect your existing work. Reload to retry."); });
    return () => { cancelled = true; };
  }, [id]);

  // Search
  useEffect(() => {
    if (!search) { setFound([]); return; }
    const t = setTimeout(() => {
      api.get("/ingredients", { params: { q: search } }).then((r) => setFound(r.data.slice(0, 8)));
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  // Live recalculate
  useEffect(() => {
    if (!rows.some((row) => Number(row.grams) > 0)) { setCalc(null); return; }
    if (!(Number(servingSize) > 0)) { setCalc(null); return; }
    let cancelled = false;
    const payload = {
      name: name || "Untitled",
      product_name: productName,
      serving_size_g: Number(servingSize),
      serving_measure: servingMeasure,
      servings_per_container: Number(servingsPer) || 1,
      unit_size_g: effectiveUnitGrams || null,
      ingredients: rows.filter((r) => Number(r.grams) > 0).map((r) => ({ ingredient_id: r.ingredient_id, grams: Number(r.grams) })),
      declared_soi: declaredSoi || null,
      product_type: productType,
      species: productType === "pet_topper" ? species : null,
      ash_pct: ashPct === "" ? null : Number(ashPct),
      moisture_max_pct: spec.moisture_max_pct === "" ? null : Number(spec.moisture_max_pct),
    };
    const t = setTimeout(() => {
      api.post("/calculate", payload).then((r) => { if (!cancelled) setCalc(r.data); }).catch((err) => {
        if (!cancelled) {
          setCalc(null);
          const detail = err?.response?.data?.detail;
          if (typeof detail === "string" && detail.startsWith("Nutrition unavailable:")) {
            toast.error(detail, { id: "supplier-nutrition-required" });
          }
        }
        console.error("Live recalculate failed", err);
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [rows, servingSize, servingMeasure, servingsPer, effectiveUnitGrams, name, productName, declaredSoi, productType, species, ashPct, spec.moisture_max_pct]);

  const add = (ing) => {
    if (rows.find((r) => r.ingredient_id === ing.id)) return toast.error("Already added");
    setRows([...rows, {
      ingredient_id: ing.id, grams: 1, _display: ing.name,
      _price: Number(ing.price_per_lb || 0),
      _is_liquid: !!ing.is_liquid,
      _density: Number(ing.density_g_ml || 0),
      _role: ing.functional_role || "",
    }]);
    setSearch(""); setFound([]);
  };
  const setGrams = (i, v) => setRows(rows.map((r, j) => (j === i ? { ...r, grams: v } : r)));
  const scaleBatchTo = (targetG) => {
    setBatchSizeG(targetG);
    const currentTotal = rows.reduce((a, r) => a + Number(r.grams || 0), 0);
    if (currentTotal > 0) {
      const factor = targetG / currentTotal;
      setRows((curr) => curr.map((r) => ({ ...r, grams: (Number(r.grams) * factor).toFixed(3) })));
    }
  };
  const remove = (i) => setRows(rows.filter((_, j) => j !== i));

  const totalG = useMemo(() => rows.reduce((a, r) => a + Number(r.grams || 0), 0), [rows]);

  // Is this a dry-powder product? Drives which spec fields & checklists show.
  // (Wet products — sauces, dressings, broths, marinades — don't need mesh,
  // bulk density, moisture-max, or a thermal-processing checklist meant for
  // dry blends. Embedded lab procedures ARE shown for every product type.)
  const isDryProduct = productType === "dry_blend";
  const isLiquidProduct = usesLiquidMeasurements(productType, productForm);
  // Pet Toppers mode swaps the human FDA label/panels for the AAFCO pet path.
  const isPet = productType === "pet_topper";
  const pet = calc?.pet || null;

  // Average batch density (g/mL) — weighted by ingredient mass. Rows that don't
  // declare density default to 1.0 g/mL (water-equivalent). For mostly-water
  // products this lands within ~3% of measured density, which is plenty for
  // a label display. Lab batches verify with a hydrometer.
  const avgDensity = useMemo(() => {
    if (!isLiquidProduct || rows.length === 0) return 1.0;
    return calculateAverageDensity(rows, (row) => row._density);
  }, [rows, isLiquidProduct]);

  // FDA-compliant volume conversion helpers (21 CFR 101.9(b)(7))
  const gramsToMl = (g) => (Number(g) || 0) / (avgDensity || 1);
  const gramsToFlOz = (g) => gramsToMl(g) / 29.5735;

  // Auto-compute a label-correct net weight for liquid products.
  // If the user typed a dry-style "4 oz (113g)" string, parse the grams and
  // re-render as "X fl oz (Y mL)" without overwriting their input.
  // Package weight must never be inferred from total batch / serving count.
  const displayNetWeight = useMemo(() => {
    // Extract grams from the user's typed string ("4 oz (113g)") if present.
    let grams = effectiveUnitGrams || 0;

    if (!isLiquidProduct) {
      // DRY products declare weight: oz/lb customary + g/kg metric (21 CFR 101.105).
      if (!grams) return netWeight || "";
      const metric = grams >= 1000 ? `${(grams / 1000).toFixed(grams >= 10000 ? 0 : 2)} kg` : `${grams.toFixed(0)}g`;
      const oz = grams / 28.3495;
      const customary = oz >= 16 ? `${(oz / 16).toFixed(oz / 16 >= 10 ? 0 : 1)} LB` : `${oz.toFixed(oz < 10 ? 1 : 0)} OZ`;
      return `${customary} (${metric})`;
    }

    // WET products declare fluid measure: fl oz/qt/gal customary + mL/L metric.
    // Already in fluid-ounce format? Trust it.
    if (/fl\s*oz|gal|\bqt\b|liter|litre|\bm?L\b/i.test(netWeight || "")) return netWeight;
    if (!grams && Number(unitSizeG) > 0) grams = Number(unitSizeG);
    if (grams > 0) {
      const ml = gramsToMl(grams);
      const flOz = gramsToFlOz(grams);
      const metric = ml >= 1000 ? `${(ml / 1000).toFixed(2)} L` : `${ml.toFixed(0)} mL`;
      const customary = flOz >= 128 ? `${(flOz / 128).toFixed(flOz / 128 >= 10 ? 0 : 1)} GAL`
        : flOz >= 32 ? `${(flOz / 32).toFixed(1)} QT`
        : `${flOz.toFixed(flOz < 10 ? 1 : 0)} fl oz`;
      return `${customary} (${metric})`;
    }
    return netWeight || "";
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [netWeight, effectiveUnitGrams, unitSizeG, totalG, servingsPer, isLiquidProduct, avgDensity]);

  // Cost math (based on $/lb supplier prices set on the Ingredients page)
  const GRAMS_PER_LB = 453.592;
  const costs = useMemo(() => {
    const OZ_PER_G = 1 / 28.3495;
    const lines = rows.map((r) => {
      const g = Number(r.grams) || 0;
      const pricePerLb = Number(r._price) || 0;
      const cost = (g / GRAMS_PER_LB) * pricePerLb;
      return { ingredient_id: r.ingredient_id, cost, has_price: pricePerLb > 0 };
    });
    const batchCost = lines.reduce((a, l) => a + l.cost, 0);
    const missing = lines.filter((l) => !l.has_price && rows.find((r) => r.ingredient_id === l.ingredient_id && Number(r.grams) > 0)).length;
    const totalLb = totalG / GRAMS_PER_LB;
    const totalOz = totalG * OZ_PER_G;
    const costPerLb = totalLb > 0 ? batchCost / totalLb : 0;
    const costPerOz = totalOz > 0 ? batchCost / totalOz : 0;
    const costPerServing = (Number(servingSize) || 0) > 0
      ? batchCost * (Number(servingSize) / totalG || 0) : 0;
    const markup = (Number(markupPct) || 0) / 100;
    // Units per batch — prefer the backend's floored pack yield (label_math),
    // else derive locally from net unit weight, else fall back to manual entry.
    const unitG = Number(unitSizeG) || 0;
    const serverUnits = calc?.units_per_batch?.full;
    const derivedUnits = (serverUnits != null && serverUnits > 0)
      ? serverUnits
      : (unitG > 0 && totalG > 0 ? Math.max(1, Math.floor(totalG / unitG)) : 0);
    const units = derivedUnits > 0 ? derivedUnits : Math.max(Number(unitsPerBatch) || 1, 1);
    const ingredientCogsPerUnit = batchCost / units;
    // Fully-loaded COGS adds packaging + labor + co-packer + overhead on a per-unit basis
    const pkg = Number(packagingPerUnit) || 0;
    const lab = Number(laborPerUnit) || 0;
    const cop = Number(copackerPerUnit) || 0;
    const ovh = Number(overheadPerUnit) || 0;
    const fullyLoadedCogsPerUnit = ingredientCogsPerUnit + pkg + lab + cop + ovh;
    // If a retail-price override is set, use it directly; else derive from markup
    const overrideRetail = Number(retailOverride) || 0;
    const suggestedRetailPerUnit = overrideRetail > 0
      ? overrideRetail
      : fullyLoadedCogsPerUnit * (1 + markup);
    const grossMarginPerUnit = suggestedRetailPerUnit - fullyLoadedCogsPerUnit;
    const grossMarginPct = suggestedRetailPerUnit > 0
      ? (grossMarginPerUnit / suggestedRetailPerUnit) * 100 : 0;
    const profitPerBatch = grossMarginPerUnit * units;
    return {
      lines, batchCost, missing, costPerLb, costPerOz, costPerServing,
      ingredientCogsPerUnit,
      packagingPerUnit: pkg, laborPerUnit: lab, copackerPerUnit: cop, overheadPerUnit: ovh,
      fullyLoadedCogsPerUnit,
      // Backwards-compat alias used by print template
      cogsPerUnit: fullyLoadedCogsPerUnit,
      suggestedRetailPerUnit, grossMarginPerUnit, grossMarginPct, profitPerBatch,
      markup, units, derivedUnits, unitG,
    };
  }, [rows, totalG, servingSize, markupPct, unitsPerBatch, unitSizeG,
      packagingPerUnit, laborPerUnit, copackerPerUnit, overheadPerUnit, retailOverride]);
  const costByIngredient = useMemo(
    () => Object.fromEntries(costs.lines.map((l) => [l.ingredient_id, l.cost])),
    [costs.lines]
  );

  const save = async (asCopy = false) => {
    if (!loaded || saving) return;
    asCopy = asCopy === true;
    const captured = draftValues;
    setSaveError("");
    const validationError = validateRecipeSave({ name, rows, servingSize, servingsPer });
    if (validationError) {
      setSaveError(validationError);
      toast.error(validationError);
      if (validationError.includes("serving size")) {
        document.querySelector('[data-testid="serving-size-input"]')?.focus();
      }
      return;
    }
    // Pre-save guard — block if any selected lot is expired
    const expiredRows = rows.filter((r) => r.lot_id && r._lot_status === "expired");
    if (expiredRows.length > 0) {
      const proceed = window.confirm(
        `${expiredRows.length} ingredient${expiredRows.length > 1 ? "s have" : " has"} an EXPIRED lot selected. ` +
        `This is a traceability red flag. Save anyway?`
      );
      if (!proceed) return;
    }
    setSaving(true);
    try {
      const payload = {
        request_id: asCopy ? crypto.randomUUID() : requestId,
        expected_updated_at: savedStamp,
        name, product_name: productName,
        serving_size_g: Number(servingSize), servings_per_container: Number(servingsPer),
        serving_measure: servingMeasure,
        ingredients: rows.filter((r) => Number(r.grams) > 0).map((r) => ({
          ingredient_id: r.ingredient_id,
          grams: Number(r.grams),
          supplier_spec_id: r.supplier_spec_id || null,
          lot_id: r.lot_id || null,
          lot_number: r.lot_number || null,
        })),
        sku, version, status, parent_recipe_id: parentRecipeId,
        mode, notes,
        product_type: productType,
        product_form: productType === "dairy_plant" ? (productForm || null) : null,
        species: productType === "pet_topper" ? species : null,
        ash_pct: ashPct === "" ? null : Number(ashPct),
        production_method: productionMethod,
        lab_production_method: labProductionMethod,
        lab_bench_procedure: labBenchProcedure,
        declared_soi: declaredSoi,
        ph: ph === "" ? null : Number(ph),
        water_activity: waterActivity === "" ? null : Number(waterActivity),
        thermal_temp_f: thermalTempF === "" ? null : Number(thermalTempF),
        thermal_hold_min: thermalHoldMin === "" ? null : Number(thermalHoldMin),
        acidified: acidified,
        kill_step: killStep || null,
        distribution_mode: distributionMode || null,
        // Spec sheet fields (coerce numeric strings, send "" → null where appropriate)
        description: spec.description,
        aroma_profile: spec.aroma_profile,
        flavor_profile: spec.flavor_profile,
        color: spec.color,
        mesh_size: spec.mesh_size,
        bulk_density_g_ml: spec.bulk_density_g_ml === "" ? null : Number(spec.bulk_density_g_ml),
        moisture_max_pct: spec.moisture_max_pct === "" ? null : Number(spec.moisture_max_pct),
        shelf_life_months: spec.shelf_life_months === "" ? null : Number(spec.shelf_life_months),
        storage_conditions: spec.storage_conditions,
        packaging: spec.packaging,
        micro_spec: spec.micro_spec,
        application_notes: spec.application_notes,
        // Pack-out & cost economics
        unit_label: unitLabel || "",
        unit_size_g: effectiveUnitGrams || null,
        net_weight: netWeight,
        packaging_cost_per_unit: Number(packagingPerUnit) || 0,
        labor_cost_per_unit: Number(laborPerUnit) || 0,
        copacker_cost_per_unit: Number(copackerPerUnit) || 0,
        overhead_cost_per_unit: Number(overheadPerUnit) || 0,
        markup_pct: Number(markupPct) || 0,
        retail_price_override: retailOverride === "" ? null : Number(retailOverride),
        intended_use_category: intendedUseCategory || "meat_seafood",
        // Flavor Architecture — only send when at least one value is set
        flavor_architecture:
          (flavorArch.front || flavorArch.body || flavorArch.finish || flavorArch.thermal)
            ? flavorArch : null,
      };
      if (id && !asCopy) {
        const { data } = await api.put(`/recipes/${id}`, payload);
        setSavedStamp(data.updated_at);
        draft.saved(captured);
        toast.success(data.history_storage === "embedded"
          ? "Saved — version history secured with your formula. R&D history view awaits a database update."
          : "Saved — formula history preserved",
          { duration: data.history_storage === "embedded" ? 8000 : 4000 });
      } else {
        if (asCopy) payload.name += " — recovered copy";
        const { data } = await api.post("/recipes", payload);
        draft.saved(captured);
        toast.success(data.history_storage === "embedded"
          ? "Created — version history secured with your formula. R&D history view awaits a database update."
          : "Created", { duration: data.history_storage === "embedded" ? 8000 : 4000 });
        navigate(`/builder/${data.id}`);
      }
    } catch (e) {
      const message = recipeSaveError(e);
      setSaveError(message);
      toast.error(message, { duration: 10000 });
    }
    finally { setSaving(false); }
  };

  // ── Generate / copy a public share link to this recipe's Spec Sheet ──
  // The link works without an account so the user can forward it to a
  // buyer / co-packer / investor. Every share carries the "Made with
  // Alaine's Bench" footer — top-of-funnel acquisition loop.
  const shareSpecSheet = async () => {
    if (!id) {
      toast.error("Save the formulation first to share its spec sheet");
      return;
    }
    try {
      const { data } = await api.post(`/recipes/${id}/share`);
      const url = `${window.location.origin}${data.path}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied to clipboard", {
          description: url,
          action: { label: "Open", onClick: () => window.open(url, "_blank", "noopener") },
          duration: 8000,
        });
      } catch {
        // Clipboard blocked (Safari private mode, iframe, etc.) — fall back.
        toast.success("Share link ready", {
          description: url,
          action: { label: "Open", onClick: () => window.open(url, "_blank", "noopener") },
          duration: 10000,
        });
      }
    } catch (err) {
      if (err?.handledToast) return;
      toast.error("Could not generate share link");
    }
  };

  const printSpecSheet = async () => {
    if (isDemo) {
      toast.info("Printing is not available in the demo. Sign in to print spec sheets.");
      return;
    }
    if (!id) {
      toast.error("Save the formulation first to generate a spec sheet");
      return;
    }
    if (rows.length === 0) {
      toast.error("Add ingredients before generating spec sheet");
      return;
    }
    let data;
    try {
      const resp = await api.get(`/recipes/${id}/spec-sheet`);
      data = resp.data;
    } catch (err) {
      console.error("Spec sheet fetch failed", err);
      toast.error("Could not generate spec sheet");
      return;
    }
    const bp = data.business_profile || {};
    const r = data.recipe || {};
    const n = data.nutrition?.rounded || {};
    const stmt = data.statement?.statement || "";
    const contains = data.statement?.contains_line || "";
    const ingTable = (data.ingredient_table || [])
      .map((i, ix) => `<tr><td>${ix + 1}</td><td>${i.name}</td><td class="num">${i.grams.toFixed(2)}</td><td class="num">${i.percent.toFixed(2)}%</td></tr>`)
      .join("");
    const styles = `
      body { font: 11px/1.4 Helvetica, Arial, sans-serif; color:#111; padding: 24px; max-width: 760px; margin: auto; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      h2 { font-size: 13px; margin: 18px 0 6px; padding-bottom: 4px; border-bottom: 2px solid #17324D; color: #17324D; text-transform: uppercase; letter-spacing: 0.05em; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #111; }
      .meta { font-size: 10px; color: #555; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 18px; }
      .grid .row { display: grid; grid-template-columns: 130px 1fr; gap: 8px; padding: 3px 0; border-bottom: 1px dotted #ddd; }
      .grid .label { color: #666; font-weight: 600; text-transform: uppercase; font-size: 9px; letter-spacing: 0.04em; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      table th, table td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
      table th { background: #f5f5f4; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
      .num { text-align: right; font-family: monospace; }
      .stmt { background: #f5f5f4; padding: 8px 10px; font-family: monospace; font-size: 10px; white-space: pre-wrap; }
      .contains { font-weight: bold; color: #991b1b; margin-top: 6px; font-size: 10px; }
      .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 9px; color: #666; line-height: 1.5; }
      @media print { body { padding: 18px; } @page { size: letter; margin: 12mm; } }
    `;
    const html = `
      <div class="ab-strip" style="background:#17324D;color:#EAF0F6;padding:6px 12px;font-size:9px;letter-spacing:0.18em;text-transform:uppercase;font-weight:600;margin:-12px -12px 14px;display:flex;justify-content:space-between;">
        <span>ALAINE'S BENCH™ · BUILT ON FDA LABELING RULES</span>
        <span style="color:#86efac;">Spec Sheet</span>
      </div>
      <div class="header">
        <div>
          <div class="meta" style="text-transform:uppercase;letter-spacing:0.15em;color:#17324D;font-weight:bold;">Product Spec Sheet</div>
          <h1>${r.product_name || r.name}</h1>
          <div class="meta">SKU: <b>${r.sku || "—"}</b> · Version: <b>${r.version || "v1"}</b> · Status: <b>${r.status || "R&D"}</b></div>
        </div>
        <div style="text-align:right;">
          <div style="font-weight:bold;">${bp.brand_name || "Alaine & Co."}</div>
          <div class="meta">${bp.legal_entity || ""}</div>
          <div class="meta">${[bp.address_line1, bp.city, bp.state, bp.zip_code].filter(Boolean).join(", ")}</div>
          <div class="meta">${bp.website || ""}</div>
        </div>
      </div>

      <h2>Identity</h2>
      <div class="grid">
        <div class="row"><div class="label">Description</div><div>${r.description || "—"}</div></div>
        <div class="row"><div class="label">Application</div><div>${r.application_notes || "—"}</div></div>
      </div>

      <h2>Sensory Profile</h2>
      <div class="grid">
        <div class="row"><div class="label">Aroma</div><div>${r.aroma_profile || "—"}</div></div>
        <div class="row"><div class="label">Flavor</div><div>${r.flavor_profile || "—"}</div></div>
        <div class="row"><div class="label">Color</div><div>${r.color || "—"}</div></div>
      </div>

      <h2>Physical / Chemical</h2>
      <div class="grid">
        ${r.product_type === "dry_blend" ? `
          <div class="row"><div class="label">Particle Size</div><div>${r.mesh_size || "—"}</div></div>
          <div class="row"><div class="label">Bulk Density</div><div>${r.bulk_density_g_ml ? r.bulk_density_g_ml + " g/mL" : "—"}</div></div>
          <div class="row"><div class="label">Moisture (max)</div><div>${r.moisture_max_pct != null ? r.moisture_max_pct + "%" : "—"}</div></div>
        ` : ""}
        <div class="row"><div class="label">Salt Content</div><div><b>${data.salt.pct}%</b> · Tolerance ${data.salt.band_label}</div></div>
      </div>

      <h2>Microbiological</h2>
      ${r.micro_spec ? `
        <div class="stmt">${r.micro_spec.replace(/</g, "&lt;")}</div>
      ` : `
        <table>
          <thead><tr><th>Test</th><th>Industry Target (Dry Seasoning Blend)</th></tr></thead>
          <tbody>
            <tr><td>Total Plate Count (TPC)</td><td>&lt; 100,000 CFU/g</td></tr>
            <tr><td>Yeast &amp; Mold</td><td>&lt; 1,000 CFU/g</td></tr>
            <tr><td>Coliforms</td><td>&lt; 100 CFU/g</td></tr>
            <tr><td>E. coli</td><td>Negative / Not detected</td></tr>
            <tr><td>Salmonella spp.</td><td>Negative in 25 g</td></tr>
            <tr><td>Listeria spp.</td><td>Negative in 25 g</td></tr>
            <tr><td>Staphylococcus aureus</td><td>&lt; 100 CFU/g</td></tr>
          </tbody>
        </table>
        <div class="contains" style="color:#92400e;margin-top:8px;">
          Reference values only — must be tested by an accredited microbiology laboratory to confirm.
        </div>
      `}

      <h2>Ingredient Statement</h2>
      <div class="stmt">${stmt}${contains ? `<div class="contains">${contains}</div>` : ""}</div>

      <h2>Ingredient Composition (% Formula)</h2>
      <table>
        <thead><tr><th>#</th><th>Ingredient</th><th>Weight (g)</th><th>% Formula</th></tr></thead>
        <tbody>${ingTable}</tbody>
        <tfoot><tr><td colspan="2"><b>Total</b></td><td class="num"><b>${data.total_batch_g.toFixed(2)}</b></td><td class="num"><b>100.00%</b></td></tr></tfoot>
      </table>

      <h2>Nutrition (per ${r.serving_size_g}g${isLiquidProduct ? ` / ${gramsToMl(r.serving_size_g).toFixed(0)} mL` : ""} serving)</h2>
      <table>
        <tr><td>Calories</td><td class="num">${n.calories}</td><td>Sodium (mg)</td><td class="num">${n.sodium_mg}</td></tr>
        <tr><td>Total Fat (g)</td><td class="num">${n.total_fat_g}</td><td>Total Carbs (g)</td><td class="num">${n.total_carbs_g}</td></tr>
        <tr><td>Saturated Fat (g)</td><td class="num">${n.saturated_fat_g}</td><td>Sugars (g)</td><td class="num">${n.sugars_g}</td></tr>
        <tr><td>Trans Fat (g)</td><td class="num">${n.trans_fat_g}</td><td>Protein (g)</td><td class="num">${n.protein_g}</td></tr>
      </table>

      <h2>Cost (Internal Reference)</h2>
      <table>
        <tr><td>Batch Cost</td><td class="num">$${data.cost.batch_cost.toFixed(2)}</td>
            <td>Cost / lb (co-packer basis)</td><td class="num">$${data.cost.cost_per_lb.toFixed(2)}</td></tr>
      </table>

      <h2>Storage & Packaging</h2>
      <div class="grid">
        <div class="row"><div class="label">Shelf Life</div><div>${r.shelf_life_months ? r.shelf_life_months + " months" : "—"}</div></div>
        <div class="row"><div class="label">Storage</div><div>${r.storage_conditions || "—"}</div></div>
        <div class="row"><div class="label">Packaging</div><div>${r.packaging || "—"}</div></div>
      </div>

      ${r.production_method ? `
        <h2>Production Method (commercial / co-packer)</h2>
        <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9px;">${r.production_method.replace(/</g, "&lt;")}</div>
      ` : ""}

      ${r.lab_production_method ? `
        <h2>Lab Production Method (bench-scale, °F)</h2>
        <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9px;">${r.lab_production_method.replace(/</g, "&lt;")}</div>
      ` : ""}

      ${r.lab_bench_procedure ? `
        <h2>Lab Bench Procedure</h2>
        <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9px;">${r.lab_bench_procedure.replace(/</g, "&lt;")}</div>
      ` : ""}

      <div class="footer">
        Generated by <b>Alaine's Bench™</b> · Built on FDA Labeling Rules · ${new Date().toLocaleString()}<br/>
        <b>CONFIDENTIAL — for co-manufacturer use only.</b> Tolerances and microbiological specs are starting reference values; verify with laboratory analysis.<br/>
        &copy; ${new Date().getFullYear()} Shirleys Daughter, LLC · All rights reserved · Trademark Pending.
      </div>
    `;
    const fullDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${(r.product_name || r.name || "Spec Sheet").replace(/</g, "&lt;")} — Spec Sheet</title>
      <style>${styles}</style></head><body>${html}
      <script>
        window.addEventListener('load', function(){ setTimeout(function(){ try{ window.focus(); window.print(); } catch(e){} }, 250); });
        window.addEventListener('afterprint', function(){ try{ window.frameElement && window.frameElement.parentNode.removeChild(window.frameElement); } catch(e){} });
      </script>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    iframe.srcdoc = fullDoc;
    document.body.appendChild(iframe);
    toast.success("Generating spec sheet…");
    setTimeout(() => { try { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); } catch (err) { console.error(err); } }, PRINT_IFRAME_CLEANUP_MS);
  };

  // ── Commercialization Package ─────────────────────────────────────────
  // One printable/downloadable PDF bundling everything a co-manufacturer
  // needs to take over production: formula & revision, batch scaling,
  // ingredient statement, Nutrition Facts, label, allergen declaration,
  // processing instructions, packaging, spec, and version history.
  // All dynamic values pass through esc() (standing XSS invariant).
  const generateCommercializationPackage = async () => {
    if (isDemo) {
      toast.info("The Commercialization Package is not available in the demo. Sign in to generate it.");
      return;
    }
    if (!id) {
      toast.error("Save the formulation first to generate a Commercialization Package");
      return;
    }
    if (rows.length === 0) {
      toast.error("Add ingredients before generating the package");
      return;
    }
    let specData = null;
    let allRecipes = [];
    let liveBp = bp;
    try {
      const [specResp, listResp, meResp] = await Promise.all([
        api.get(`/recipes/${id}/spec-sheet`),
        api.get("/recipes").catch(() => ({ data: [] })),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      specData = specResp.data;
      allRecipes = Array.isArray(listResp.data) ? listResp.data : [];
      if (meResp.data?.business_profile) liveBp = meResp.data.business_profile;
    } catch (err) {
      console.error("Commercialization package fetch failed", err);
      toast.error("Could not generate the Commercialization Package");
      return;
    }
    const r = specData.recipe || {};
    const nRound = specData.nutrition?.rounded || {};
    const stmt = esc(specData.statement?.statement || "");
    const contains = esc(specData.statement?.contains_line || "");
    const totalBatchG = Number(specData.total_batch_g) || 0;
    const ingTableRows = specData.ingredient_table || [];

    // Version history — every formulation sharing this lineage root.
    const rootId = r.parent_recipe_id || r.id;
    const lineage = allRecipes
      .filter((x) => x.id === rootId || x.parent_recipe_id === rootId)
      .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));

    // Batch scaling — percentage-based scale-up table for common batch targets.
    const SCALE_TARGETS = [
      { label: "1 kg (bench)", g: 1000 },
      { label: "5 kg (pilot)", g: 5000 },
      { label: "25 lb (drum)", g: 25 * 453.592 },
      { label: "100 lb (production)", g: 100 * 453.592 },
    ];
    const scalingRows = ingTableRows.map((i) => {
      const pct = Number(i.percent) || 0;
      const cells = SCALE_TARGETS
        .map((t) => `<td class="num">${(pct / 100 * t.g).toFixed(1)}</td>`)
        .join("");
      return `<tr><td>${esc(i.name)}</td><td class="num">${pct.toFixed(2)}%</td>${cells}</tr>`;
    }).join("");

    const formulaRows = ingTableRows.map((i, ix) =>
      `<tr><td>${ix + 1}</td><td>${esc(i.name)}</td><td class="num">${Number(i.grams).toFixed(2)}</td><td class="num">${Number(i.percent).toFixed(2)}%</td></tr>`
    ).join("");

    // Allergen declaration — the Contains line plus a plain summary.
    const allergenBlock = contains
      ? `<div class="stmt"><b>${contains}</b></div>`
      : `<div class="stmt">No major food allergens detected in the ingredient database for this formula. The manufacturer must verify allergen status (including cross-contact) against actual supplier documentation.</div>`;

    const versionRows = (lineage.length ? lineage : [r]).map((v) =>
      `<tr>
        <td>${esc(v.version || "v1")}${v.id === r.id ? " <b>(this document)</b>" : ""}</td>
        <td>${esc(v.status || "R&D")}</td>
        <td>${esc(v.sku || "—")}</td>
        <td>${v.created_at ? esc(new Date(v.created_at).toLocaleDateString()) : "—"}</td>
        <td>${v.updated_at ? esc(new Date(v.updated_at).toLocaleDateString()) : "—"}</td>
        <td>${esc(v.notes || "—")}</td>
      </tr>`
    ).join("");

    // FDA Nutrition Facts panel + label preview (human-food path only).
    const n = calc?.nutrition?.per_serving;
    const dv = calc?.nutrition?.daily_values;
    const fmt = (v, u = "g") => v === 0 ? `0${u}` : v === 0.5 ? `<1${u}` : `${esc(v)}${u}`;
    const rawLogo = liveBp.logo_data || "";
    const logo = SAFE_LOGO_RE.test(rawLogo) ? rawLogo : "";
    const brand = liveBp.brand_name ? esc(String(liveBp.brand_name).toUpperCase()) : "";
    const safeProductName = esc(r.product_name || r.name || "");
    const safeNetWeight = esc(displayNetWeight);
    const addressBlock = (liveBp.legal_entity || liveBp.address_line1) ?
      `${esc(liveBp.responsible_party_prefix || "Distributed by")} ${esc(liveBp.legal_entity || "")}<br/>` +
      `${esc(liveBp.address_line1 || "")}${liveBp.address_line2 ? `, ${esc(liveBp.address_line2)}` : ""}<br/>` +
      `${esc(liveBp.city || "")}${liveBp.city && liveBp.state ? ", " : ""}${esc(liveBp.state || "")} ${esc(liveBp.zip_code || "")}` +
      (liveBp.website ? `<br/>${esc(liveBp.website)}` : "") : "";
    const labelPanel = (!isPet && n && dv) ? `
      <div class="label">
        ${brand || safeProductName ? `
          <div class="lheader">
            ${logo ? `<img class="logo" src="${logo}" alt=""/>` : ""}
            ${brand ? `<div class="lbrand">${brand}</div>` : ""}
            ${safeProductName ? `<div class="lproduct">${safeProductName}</div>` : ""}
            <div class="lnetwt">NET WT ${safeNetWeight}</div>
          </div>` : ""}
        <div class="nutbox">
          <div class="nf-title">Nutrition Facts</div>
          <div class="nf-spc">${esc(servingCountDisplay)}</div>
          <div class="nf-rule-thin"><b>Serving size</b><span>${esc(servingSizeDisplay)}</span></div>
          <div class="nf-amount">Amount per serving</div>
          <div class="nf-cal-row"><span>Calories</span><span class="cal">${esc(n.calories)}</span></div>
          <div class="nf-dv">% Daily Value*</div>
          <div class="nf-row"><span><b>Total Fat</b> ${fmt(n.total_fat_g)}</span><b>${esc(dv.total_fat_g)}%</b></div>
          <div class="nf-row indent"><span>Saturated Fat ${fmt(n.saturated_fat_g)}</span><b>${esc(dv.saturated_fat_g)}%</b></div>
          <div class="nf-row indent"><span><i>Trans</i> Fat ${fmt(n.trans_fat_g)}</span></div>
          <div class="nf-row"><span><b>Cholesterol</b> ${fmt(n.cholesterol_mg, "mg")}</span><b>${esc(dv.cholesterol_mg)}%</b></div>
          <div class="nf-row"><span><b>Sodium</b> ${fmt(n.sodium_mg, "mg")}</span><b>${esc(dv.sodium_mg)}%</b></div>
          <div class="nf-row"><span><b>Total Carbohydrate</b> ${fmt(n.total_carbs_g)}</span><b>${esc(dv.total_carbs_g)}%</b></div>
          <div class="nf-row indent"><span>Dietary Fiber ${fmt(n.fiber_g)}</span><b>${esc(dv.fiber_g)}%</b></div>
          <div class="nf-row indent"><span>Total Sugars ${fmt(n.sugars_g)}</span></div>
          <div class="nf-row indent2"><span>Includes ${fmt(n.added_sugars_g)} Added Sugars</span><b>${esc(dv.added_sugars_g)}%</b></div>
          <div class="nf-rule-mid"></div>
          <div class="nf-row"><span><b>Protein</b> ${fmt(n.protein_g)}</span></div>
          <div class="nf-rule-thin2"></div>
          <div class="nf-micro"><span>Vitamin D ${esc(n.vitamin_d_mcg)}mcg</span><span>${esc(dv.vitamin_d_mcg)}%</span></div>
          <div class="nf-micro"><span>Calcium ${esc(n.calcium_mg)}mg</span><span>${esc(dv.calcium_mg)}%</span></div>
          <div class="nf-micro"><span>Iron ${fmt(n.iron_mg, "mg")}</span><span>${esc(dv.iron_mg)}%</span></div>
          <div class="nf-micro last"><span>Potassium ${esc(n.potassium_mg)}mg</span><span>${esc(dv.potassium_mg)}%</span></div>
          <div class="nf-foot">* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.</div>
        </div>
        <div class="ingbox">
          <div><b>${stmt}</b></div>
          ${contains ? `<div class="lcontains"><b>${contains}</b></div>` : ""}
          ${addressBlock ? `<div class="laddress">${addressBlock}</div>` : ""}
        </div>
      </div>` : "";

    const specGrid = `
      <div class="grid">
        <div class="row"><div class="glabel">Description</div><div>${esc(r.description || "—")}</div></div>
        <div class="row"><div class="glabel">Application</div><div>${esc(r.application_notes || "—")}</div></div>
        <div class="row"><div class="glabel">Aroma</div><div>${esc(r.aroma_profile || "—")}</div></div>
        <div class="row"><div class="glabel">Flavor</div><div>${esc(r.flavor_profile || "—")}</div></div>
        <div class="row"><div class="glabel">Color</div><div>${esc(r.color || "—")}</div></div>
        ${r.product_type === "dry_blend" ? `
          <div class="row"><div class="glabel">Particle Size</div><div>${esc(r.mesh_size || "—")}</div></div>
          <div class="row"><div class="glabel">Bulk Density</div><div>${r.bulk_density_g_ml ? esc(r.bulk_density_g_ml) + " g/mL" : "—"}</div></div>
          <div class="row"><div class="glabel">Moisture (max)</div><div>${r.moisture_max_pct != null ? esc(r.moisture_max_pct) + "%" : "—"}</div></div>
        ` : ""}
        <div class="row"><div class="glabel">Salt Content</div><div><b>${esc(specData.salt?.pct)}%</b> · Tolerance ${esc(specData.salt?.band_label || "—")}</div></div>
        <div class="row"><div class="glabel">Shelf Life</div><div>${r.shelf_life_months ? esc(r.shelf_life_months) + " months" : "—"}</div></div>
        <div class="row"><div class="glabel">Storage</div><div>${esc(r.storage_conditions || "—")}</div></div>
        <div class="row"><div class="glabel">Micro Spec</div><div>${esc(r.micro_spec || "Industry reference targets — must be confirmed by an accredited lab")}</div></div>
      </div>`;

    const html = `
      <div class="ab-strip" style="background:#17324D;color:#EAF0F6;padding:6px 12px;font-size:9px;letter-spacing:0.18em;text-transform:uppercase;font-weight:600;margin:-12px -12px 14px;display:flex;justify-content:space-between;">
        <span>ALAINE'S BENCH™ · BUILT ON FDA LABELING RULES</span>
        <span style="color:#86efac;">Commercialization Package</span>
      </div>
      <div class="header">
        <div>
          <div class="meta" style="text-transform:uppercase;letter-spacing:0.15em;color:#17324D;font-weight:bold;">Commercialization Package — Co-Manufacturer Handoff</div>
          <h1>${safeProductName || esc(r.name || "Untitled formulation")}</h1>
          <div class="meta">SKU: <b>${esc(r.sku || "—")}</b> · Revision: <b>${esc(r.version || "v1")}</b> · Status: <b>${esc(r.status || "R&D")}</b> · Product type: <b>${esc((r.product_type || "dry_blend").replace(/_/g, " "))}</b></div>
        </div>
        <div style="text-align:right;">
          <div style="font-weight:bold;">${esc(liveBp.brand_name || "Alaine & Co.")}</div>
          <div class="meta">${esc(liveBp.legal_entity || "")}</div>
          <div class="meta">${[liveBp.address_line1, liveBp.city, liveBp.state, liveBp.zip_code].filter(Boolean).map(esc).join(", ")}</div>
          <div class="meta">${esc(liveBp.website || "")}</div>
        </div>
      </div>

      <h2>1. Formula &amp; Revision</h2>
      <div class="meta" style="margin-bottom:6px;">Reference batch: <b>${totalBatchG.toFixed(2)} g</b> (${(totalBatchG / 28.3495).toFixed(2)} oz / ${(totalBatchG / 453.592).toFixed(3)} lb) · Serving size: <b>${esc(r.serving_size_g)} g</b> · Ordered by weight, descending.</div>
      <table>
        <thead><tr><th>#</th><th>Ingredient</th><th>Weight (g)</th><th>% Formula</th></tr></thead>
        <tbody>${formulaRows}</tbody>
        <tfoot><tr><td colspan="2"><b>Total</b></td><td class="num"><b>${totalBatchG.toFixed(2)}</b></td><td class="num"><b>100.00%</b></td></tr></tfoot>
      </table>

      <h2>2. Batch Scaling</h2>
      <div class="meta" style="margin-bottom:6px;">Scale-up computed from formula percentages. Verify blend uniformity and process losses at each scale.</div>
      <table>
        <thead><tr><th>Ingredient</th><th>% Formula</th>${SCALE_TARGETS.map((t) => `<th>${t.label} (g)</th>`).join("")}</tr></thead>
        <tbody>${scalingRows}</tbody>
      </table>

      <h2>3. Ingredient Statement (FDA, descending weight)</h2>
      <div class="stmt">${stmt}</div>

      <h2>4. Allergen Declaration</h2>
      ${allergenBlock}

      ${(!isPet && n && dv) ? `
        <h2>5. Nutrition Facts &amp; Label</h2>
        <div class="meta" style="margin-bottom:6px;">FDA-rounded per 21 CFR 101.9. Vertical panel preview as it appears on the retail label.</div>
        ${labelPanel}
      ` : `
        <h2>5. Nutrition Facts &amp; Label</h2>
        <div class="stmt">Human-food FDA Nutrition Facts are not applicable to this product type. See the product's Guaranteed Analysis / AAFCO documentation.</div>
      `}

      <h2>6. Processing Instructions</h2>
      ${r.production_method ? `
        <div class="meta" style="margin-bottom:4px;"><b>Commercial / co-packer scale</b></div>
        <div class="stmt mono">${esc(r.production_method)}</div>
      ` : `<div class="stmt">No commercial production method on file — the co-manufacturer must develop and validate the production process.</div>`}
      ${r.lab_production_method ? `
        <div class="meta" style="margin:8px 0 4px;"><b>Lab / bench scale (°F)</b></div>
        <div class="stmt mono">${esc(r.lab_production_method)}</div>
      ` : ""}
      ${r.lab_bench_procedure ? `
        <div class="meta" style="margin:8px 0 4px;"><b>Bench-side QA procedure</b></div>
        <div class="stmt mono">${esc(r.lab_bench_procedure)}</div>
      ` : ""}

      <h2>7. Packaging</h2>
      <div class="grid">
        <div class="row"><div class="glabel">Retail Unit</div><div>${esc(r.unit_label || "—")}${r.unit_size_g ? ` · ${esc(r.unit_size_g)} g net` : ""}</div></div>
        <div class="row"><div class="glabel">Net Weight (label)</div><div>${safeNetWeight || "—"}</div></div>
        <div class="row"><div class="glabel">Bulk Packaging</div><div>${esc(r.packaging || "—")}</div></div>
        <div class="row"><div class="glabel">Storage</div><div>${esc(r.storage_conditions || "—")}</div></div>
      </div>

      <h2>8. Product Specification</h2>
      ${specGrid}

      <h2>9. Version History</h2>
      <table>
        <thead><tr><th>Revision</th><th>Status</th><th>SKU</th><th>Created</th><th>Last updated</th><th>Notes</th></tr></thead>
        <tbody>${versionRows}</tbody>
      </table>

      <div class="footer">
        Generated by <b>Alaine's Bench™</b> · Built on FDA Labeling Rules · ${new Date().toLocaleString()}<br/>
        <b>CONFIDENTIAL — for co-manufacturer use only.</b> Nutrition values are calculated from ingredient database values using FDA rounding rules (21 CFR 101.9).
        Microbiological and tolerance values are starting reference points and must be verified by accredited laboratory analysis.
        <b>Final label review is the responsibility of the manufacturer.</b><br/>
        &copy; ${new Date().getFullYear()} Shirleys Daughter, LLC · All rights reserved · Trademark Pending.
      </div>
    `;

    const styles = `
      body { font: 11px/1.4 Helvetica, Arial, sans-serif; color:#111; padding: 24px; max-width: 760px; margin: auto; }
      h1 { font-size: 20px; margin: 0 0 4px; }
      h2 { font-size: 13px; margin: 18px 0 6px; padding-bottom: 4px; border-bottom: 2px solid #17324D; color: #17324D; text-transform: uppercase; letter-spacing: 0.05em; }
      .header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #111; }
      .meta { font-size: 10px; color: #555; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 18px; }
      .grid .row { display: grid; grid-template-columns: 130px 1fr; gap: 8px; padding: 3px 0; border-bottom: 1px dotted #ddd; }
      .grid .glabel { color: #666; font-weight: 600; text-transform: uppercase; font-size: 9px; letter-spacing: 0.04em; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      table th, table td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
      table th { background: #f5f5f4; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; }
      table tfoot td { background: #f5f5f4; font-weight: 700; }
      .num { text-align: right; font-family: monospace; }
      .stmt { background: #f5f5f4; padding: 8px 10px; font-size: 10px; white-space: pre-wrap; }
      .stmt.mono { font-family: monospace; font-size: 9px; }
      .footer { margin-top: 24px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 9px; color: #666; line-height: 1.5; }
      /* FDA label preview */
      .label { max-width: 340px; margin-top: 4px; }
      .lheader { border: 3px solid black; border-bottom: 0; text-align: center; padding: 6px 8px 4px; }
      .lheader .logo { max-width: 120px; max-height: 60px; object-fit: contain; margin-bottom: 4px; }
      .lbrand { font-size: 15px; font-weight: 900; letter-spacing: -0.02em; }
      .lproduct { font-size: 13px; font-weight: 700; }
      .lnetwt { font-size: 10px; margin-top: 2px; }
      .nutbox { border: 3px solid black; padding: 8px; }
      .nf-title { font-size: 28px; font-weight: 900; line-height: 1; letter-spacing: -0.02em; }
      .nf-spc { font-size: 11px; }
      .nf-rule-thin { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 8px solid black; padding-bottom: 1px; font-size: 13px; font-weight: bold; }
      .nf-amount { font-size: 10px; margin-top: 4px; }
      .nf-cal-row { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 4px solid black; padding-bottom: 1px; font-weight: 900; font-size: 22px; }
      .nf-cal-row .cal { font-size: 24px; }
      .nf-dv { text-align: right; font-size: 10px; font-weight: bold; margin-top: 2px; }
      .nf-row { display:flex; justify-content:space-between; border-bottom: 1px solid black; padding: 1px 0; font-size: 13px; }
      .nf-row.indent { padding-left: 16px; }
      .nf-row.indent2 { padding-left: 32px; }
      .nf-rule-mid { border-bottom: 8px solid black; }
      .nf-rule-thin2 { border-bottom: 3px solid black; margin-top: 1px; }
      .nf-micro { display:flex; justify-content:space-between; border-bottom: 1px solid black; padding: 1px 0; font-size: 12px; }
      .nf-micro.last { border-bottom: 0; }
      .nf-foot { font-size: 9px; line-height: 1.2; margin-top: 8px; }
      .ingbox { border: 3px solid black; border-top: 0; padding: 6px 8px; font-size: 9px; line-height: 1.3; }
      .ingbox .lcontains { margin-top: 4px; }
      .ingbox .laddress { margin-top: 6px; padding-top: 6px; border-top: 1px solid black; font-size: 8.5px; }
      @media print { body { padding: 18px; } @page { size: letter; margin: 12mm; } h2 { break-after: avoid; } table, .label { break-inside: avoid; } }
    `;
    const fullDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${esc(r.product_name || r.name || "Formulation")} — Commercialization Package</title>
      <style>${styles}</style></head><body>${html}
      <script>
        window.addEventListener('load', function(){ setTimeout(function(){ try{ window.focus(); window.print(); } catch(e){} }, 250); });
        window.addEventListener('afterprint', function(){ try{ window.frameElement && window.frameElement.parentNode.removeChild(window.frameElement); } catch(e){} });
      </script>
      </body></html>`;
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    iframe.srcdoc = fullDoc;
    document.body.appendChild(iframe);
    toast.success("Generating Commercialization Package…", {
      description: "Use your browser's Save as PDF in the print dialog to download one file.",
    });
    setTimeout(() => { try { if (iframe.parentNode) iframe.parentNode.removeChild(iframe); } catch (err) { console.error(err); } }, PRINT_IFRAME_CLEANUP_MS);
  };

  const printFormula = () => {
    if (isDemo) {
      toast.info("Printing is not available in the demo. Sign in to print formulations.");
      return;
    }
    if (rows.length === 0) {
      toast.error("Add ingredients before printing");
      return;
    }
    const safeContains = esc(calc?.statement?.contains_line);
    const totalGrams = rows.reduce((a, r) => a + Number(r.grams || 0), 0);
    const ingredientRows = rows
      .map((r, i) => {
        const g = Number(r.grams) || 0;
        const pct = totalGrams ? (g / totalGrams * 100) : 0;
        const lb = g / 453.592;
        const pricePerLb = Number(r._price) || 0;
        const lineCost = lb * pricePerLb;
        return `<tr>
          <td>${i + 1}</td>
          <td class="name">${esc(r._display)}</td>
          <td class="num">${g.toFixed(2)}</td>
          <td class="num">${lb.toFixed(4)}</td>
          <td class="num">${pct.toFixed(2)}%</td>
          <td class="num">${pricePerLb > 0 ? `$${lineCost.toFixed(4)}` : "—"}</td>
        </tr>`;
      }).join("");
    const batchCost = rows.reduce((a, r) => a + ((Number(r.grams) || 0) / 453.592) * (Number(r._price) || 0), 0);
    const totalLb = totalGrams / 453.592;
    const costPerLb = totalLb > 0 ? batchCost / totalLb : 0;
    const costPerOz = totalGrams > 0 ? batchCost / (totalGrams / 28.3495) : 0;
    const costPerServing = totalGrams > 0 && Number(servingSize) > 0 ? batchCost * (Number(servingSize) / totalGrams) : 0;
    // Units a batch yields = floor(batch weight / net unit weight); a partial
    // pack cannot be sold. Fall back to the manual count only when no net unit
    // weight is set.
    const printUnitG = Number(unitSizeG) || 0;
    const printDerivedUnits = printUnitG > 0 && totalGrams > 0 ? Math.max(1, Math.floor(totalGrams / printUnitG)) : 0;
    const units = printDerivedUnits > 0 ? printDerivedUnits : Math.max(Number(unitsPerBatch) || 1, 1);
    const ingredientCogsPerUnit = batchCost / units;
    const pkg = Number(packagingPerUnit) || 0;
    const lab = Number(laborPerUnit) || 0;
    const cop = Number(copackerPerUnit) || 0;
    const ovh = Number(overheadPerUnit) || 0;
    const cogsPerUnit = ingredientCogsPerUnit + pkg + lab + cop + ovh;
    const retailPerUnit = cogsPerUnit * (1 + (Number(markupPct) || 0) / 100);
    const marginPerUnit = retailPerUnit - cogsPerUnit;
    const marginPct = retailPerUnit > 0 ? (marginPerUnit / retailPerUnit) * 100 : 0;
    const profitPerBatch = marginPerUnit * units;

    const html = `
      <div class="sheet">
        <div class="ab-strip" style="background:#17324D;color:#EAF0F6;padding:6px 12px;font-size:9px;letter-spacing:0.18em;text-transform:uppercase;font-weight:600;margin-bottom:14px;display:flex;justify-content:space-between;">
          <span>ALAINE'S BENCH™ · BUILT ON FDA LABELING RULES</span>
          <span style="color:#86efac;">Formulation Sheet</span>
        </div>
        <div class="hdr">
          <div class="brand-line">${esc((bp.brand_name || "Alaine & Co.").toUpperCase())}</div>
          <div class="company">${esc(bp.legal_entity || "Shirleys Daughter, LLC")}</div>
        </div>
        <h1>Formulation Sheet</h1>
        <table class="meta">
          <tr><td><b>Product:</b></td><td>${esc(productName || name)}</td>
              <td><b>SKU:</b></td><td>${esc(sku || "—")}</td></tr>
          <tr><td><b>Version:</b></td><td>${esc(version)}</td>
              <td><b>Status:</b></td><td>${esc(status)}</td></tr>
          <tr><td><b>Batch:</b></td><td>${totalGrams.toFixed(1)} g (${(totalGrams / 28.3495).toFixed(2)} oz / ${(totalGrams / 453.592).toFixed(3)} lb)</td>
              <td><b>Date:</b></td><td>${new Date().toLocaleDateString()}</td></tr>
          <tr><td><b>Serving:</b></td><td>${esc(servingSizeDisplay)}</td>
              <td><b>Servings:</b></td><td>${esc(servingCountDisplay)}</td></tr>
        </table>

        <h2>Ingredients (descending by weight)</h2>
        <table class="ing">
          <thead>
            <tr><th>#</th><th>Ingredient</th><th>Weight (g)</th><th>lb</th><th>% Formula</th><th>Cost</th></tr>
          </thead>
          <tbody>${ingredientRows}</tbody>
          <tfoot>
            <tr>
              <td colspan="2"><b>Total</b></td>
              <td class="num"><b>${totalGrams.toFixed(2)}</b></td>
              <td class="num"><b>${(totalGrams / 453.592).toFixed(4)}</b></td>
              <td class="num"><b>100.00%</b></td>
              <td class="num"><b>$${batchCost.toFixed(2)}</b></td>
            </tr>
          </tfoot>
        </table>

        <h2>Cost & Pricing</h2>
        <table class="nut">
          <tr><td><b>Batch ingredient cost</b></td><td><b>$${batchCost.toFixed(2)}</b></td>
              <td>Cost / lb (co-packer basis)</td><td>$${costPerLb.toFixed(2)}</td></tr>
          <tr><td>Cost / serving</td><td>$${costPerServing.toFixed(3)}</td>
              <td>Retail units / batch</td><td>${units}</td></tr>
          <tr><td>Ingredient cost / unit</td><td>$${ingredientCogsPerUnit.toFixed(2)}</td>
              <td>Packaging / unit</td><td>$${pkg.toFixed(2)}</td></tr>
          <tr><td>Labor / unit</td><td>$${lab.toFixed(2)}</td>
              <td>Co-packer fee / unit</td><td>$${cop.toFixed(2)}</td></tr>
          <tr><td>Overhead / unit</td><td>$${ovh.toFixed(2)}</td>
              <td><b>Fully-loaded COGS / unit</b></td><td><b>$${cogsPerUnit.toFixed(2)}</b></td></tr>
          <tr><td>Markup</td><td>${(Number(markupPct) || 0).toFixed(0)}% (${((Number(markupPct) || 0) / 100 + 1).toFixed(2)}×)</td>
              <td>Implied margin</td><td>${marginPct.toFixed(1)}%</td></tr>
          <tr><td>Suggested retail / unit</td><td><b>$${retailPerUnit.toFixed(2)}</b></td>
              <td><b>Gross margin / unit</b></td><td><b>$${marginPerUnit.toFixed(2)}</b></td></tr>
          <tr><td colspan="2"></td><td><b>Profit / batch</b></td><td><b>$${profitPerBatch.toFixed(2)}</b></td></tr>
        </table>

        ${calc?.statement?.statement ? `
          <h2>Ingredient Statement (FDA, descending weight)</h2>
          <div class="stmt">${esc(calc.statement.statement)}</div>
          ${safeContains ? `<div class="stmt"><b>${safeContains}</b></div>` : ""}
        ` : ""}

        ${calc?.nutrition?.per_serving ? `
          <h2>Per-Serving Nutrition (per ${servingSize}g${isLiquidProduct ? ` / ${gramsToMl(servingSize).toFixed(0)} mL` : ""}, FDA-rounded)</h2>
          <table class="nut">
            <tr><td>Calories</td><td>${calc.nutrition.per_serving.calories}</td>
                <td>Total Fat</td><td>${calc.nutrition.per_serving.total_fat_g}g</td></tr>
            <tr><td>Sat Fat</td><td>${calc.nutrition.per_serving.saturated_fat_g}g</td>
                <td>Trans Fat</td><td>${calc.nutrition.per_serving.trans_fat_g}g</td></tr>
            <tr><td>Cholesterol</td><td>${calc.nutrition.per_serving.cholesterol_mg}mg</td>
                <td>Sodium</td><td>${calc.nutrition.per_serving.sodium_mg}mg</td></tr>
            <tr><td>Total Carbs</td><td>${calc.nutrition.per_serving.total_carbs_g}g</td>
                <td>Fiber</td><td>${calc.nutrition.per_serving.fiber_g}g</td></tr>
            <tr><td>Sugars</td><td>${calc.nutrition.per_serving.sugars_g}g</td>
                <td>Added Sugars</td><td>${calc.nutrition.per_serving.added_sugars_g}g</td></tr>
            <tr><td>Protein</td><td>${calc.nutrition.per_serving.protein_g}g</td>
                <td>Calcium</td><td>${calc.nutrition.per_serving.calcium_mg}mg</td></tr>
          </table>
        ` : ""}

        ${productionMethod ? `
          <h2>Production Method (commercial / co-packer)</h2>
          <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9.5pt;">${productionMethod.replace(/</g, "&lt;")}</div>
        ` : ""}

        ${labProductionMethod ? `
          <h2>Lab Production Method (bench-scale, °F)</h2>
          <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9.5pt;">${labProductionMethod.replace(/</g, "&lt;")}</div>
        ` : ""}

        ${labBenchProcedure ? `
          <h2>Lab Bench Procedure (Bench-side QA)</h2>
          <div class="stmt" style="white-space:pre-wrap;font-family:monospace;font-size:9.5pt;">${labBenchProcedure.replace(/</g, "&lt;")}</div>
        ` : ""}

        <div class="footer">
          Generated by <b>Alaine's Bench™</b> · Built on FDA Labeling Rules · ${new Date().toLocaleString()}<br/>
          <b>CONFIDENTIAL — Internal R&amp;D record.</b> Not a substitute for laboratory analysis.<br/>
          &copy; ${new Date().getFullYear()} Shirleys Daughter, LLC · All rights reserved · Trademark Pending.
        </div>
      </div>`;

    const styles = `
      @page { margin: 0.5in; size: letter; }
      * { box-sizing: border-box; }
      body { margin: 0; padding: 32px; background: white; color: #111; font-family: Helvetica, Arial, sans-serif; font-size: 11pt; }
      .sheet { max-width: 7.5in; margin: 0 auto; }
      .hdr { border-bottom: 3px solid black; padding-bottom: 8px; margin-bottom: 16px; }
      .brand-line { font-size: 16pt; font-weight: 900; letter-spacing: -0.02em; }
      .company { font-size: 10pt; color: #555; }
      h1 { font-size: 22pt; margin: 8px 0 16px; font-weight: 800; }
      h2 { font-size: 12pt; margin: 18px 0 6px; padding-bottom: 4px; border-bottom: 1px solid #999; text-transform: uppercase; letter-spacing: 0.04em; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
      table.meta td { padding: 3px 8px; font-size: 10pt; }
      table.meta td:nth-child(odd) { color: #555; width: 18%; }
      table.ing th, table.ing td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; font-size: 10pt; }
      table.ing th { background: #f5f5f4; font-weight: 700; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.04em; }
      table.ing td.num { text-align: right; font-variant-numeric: tabular-nums; }
      table.ing tfoot td { background: #f5f5f4; font-weight: 700; }
      .stmt { font-size: 10pt; line-height: 1.5; padding: 6px 0; }
      table.nut td { border: 1px solid #e5e5e5; padding: 4px 8px; font-size: 10pt; width: 25%; }
      table.nut td:nth-child(odd) { font-weight: 600; background: #fafaf9; }
      .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #ccc; color: #666; font-size: 9pt; line-height: 1.5; }
    `;

    const fullDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${(productName || name || "Formulation").replace(/</g, "&lt;")} — Formulation Sheet</title>
      <style>${styles}</style></head><body>${html}
      <script>
        window.addEventListener('load', function(){
          setTimeout(function(){ try { window.focus(); window.print(); } catch(e){} }, 250);
        });
        window.addEventListener('afterprint', function(){
          try { window.frameElement && window.frameElement.parentNode.removeChild(window.frameElement); } catch(e){}
        });
      </script>
      </body></html>`;

    // Use srcdoc + visible (0-size) iframe for reliable cross-browser print.
    // NOTE: do NOT use visibility:hidden — some browsers skip print on hidden iframes.
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
    iframe.srcdoc = fullDoc;
    document.body.appendChild(iframe);
    toast.success("Opening print dialog…");
    // Safety cleanup in case afterprint never fires (e.g., user cancels on some browsers)
    setTimeout(() => {
      try {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      } catch (err) { console.error("Print iframe cleanup failed", err); }
    }, PRINT_IFRAME_CLEANUP_MS);
  };

  const print = async (presetKey = "sheet", purposeKey = "commercial") => {
    if (isDemo) {
      toast.info("Printing is not available in the demo. Sign in to print labels.");
      return;
    }
    if (!calc?.nutrition) {
      toast.error(calc?.nutrition_error || "Add ingredients to generate a label first");
      return;
    }
    if (isPet) {
      toast.info("Printable labels are for human-food FDA panels. Pet labels need AAFCO review before printing.");
      return;
    }
    // Fetch latest profile so we always have current address info
    let liveBp = bp;
    try {
      const { data } = await api.get("/auth/me");
      liveBp = data?.business_profile || {};
    } catch (err) {
      console.warn("Failed to refresh business profile for label print; using cached", err);
    }
    const n = calc.nutrition.per_serving;
    const dv = calc.nutrition.daily_values;
    const fmt = (v, u = "g") => v === 0 ? `0${u}` : v === 0.5 ? `<1${u}` : `${v}${u}`;
    const brand = liveBp.brand_name ? esc(liveBp.brand_name.toUpperCase()) : "";
    const rawLogo = liveBp.logo_data || "";
    const logo = SAFE_LOGO_RE.test(rawLogo) ? rawLogo : "";
    const preset = LABEL_PRESETS[presetKey] || LABEL_PRESETS.sheet;
    const purpose = LABEL_PURPOSES[purposeKey] || LABEL_PURPOSES.commercial;
    const stampHTML = purpose.stamp ? `<div class="stamp">${purpose.stamp}</div>` : "";
    const safeProductName = esc(productName);
    const safeNetWeight = esc(displayNetWeight);
    const safeStatement = esc(calc.statement.statement);
    const safeContains = esc(calc.statement.contains_line);
    const addressBlock = (liveBp.legal_entity || liveBp.address_line1) ?
      `${esc(liveBp.responsible_party_prefix || "Distributed by")} ${esc(liveBp.legal_entity || "")}<br/>` +
      `${esc(liveBp.address_line1 || "")}${liveBp.address_line2 ? `, ${esc(liveBp.address_line2)}` : ""}<br/>` +
      `${esc(liveBp.city || "")}${liveBp.city && liveBp.state ? ", " : ""}${esc(liveBp.state || "")} ${esc(liveBp.zip_code || "")}` +
      (liveBp.website ? `<br/>${esc(liveBp.website)}` : "") : "";

    const labelHTML = `
      <div class="label">
        ${stampHTML}
        ${brand || safeProductName ? `
          <div class="header">
            ${logo ? `<img class="logo" src="${logo}" alt=""/>` : ""}
            ${brand ? `<div class="brand">${brand}</div>` : ""}
            ${safeProductName ? `<div class="product">${safeProductName}</div>` : ""}
            <div class="netwt">NET WT ${safeNetWeight}</div>
          </div>` : ""}
        <div class="nutbox">
          <div class="nf-title">Nutrition Facts</div>
          <div class="nf-spc">${esc(servingCountDisplay)}</div>
          <div class="nf-rule-thin"><b>Serving size</b><span>${esc(servingSizeDisplay)}</span></div>
          <div class="nf-amount">Amount per serving</div>
          <div class="nf-cal-row"><span>Calories</span><span class="cal">${n.calories}</span></div>
          <div class="nf-dv">% Daily Value*</div>
          <div class="nf-row"><span><b>Total Fat</b> ${fmt(n.total_fat_g)}</span><b>${dv.total_fat_g}%</b></div>
          <div class="nf-row indent"><span>Saturated Fat ${fmt(n.saturated_fat_g)}</span><b>${dv.saturated_fat_g}%</b></div>
          <div class="nf-row indent"><span><i>Trans</i> Fat ${fmt(n.trans_fat_g)}</span></div>
          <div class="nf-row"><span><b>Cholesterol</b> ${fmt(n.cholesterol_mg, "mg")}</span><b>${dv.cholesterol_mg}%</b></div>
          <div class="nf-row"><span><b>Sodium</b> ${fmt(n.sodium_mg, "mg")}</span><b>${dv.sodium_mg}%</b></div>
          <div class="nf-row"><span><b>Total Carbohydrate</b> ${fmt(n.total_carbs_g)}</span><b>${dv.total_carbs_g}%</b></div>
          <div class="nf-row indent"><span>Dietary Fiber ${fmt(n.fiber_g)}</span><b>${dv.fiber_g}%</b></div>
          <div class="nf-row indent"><span>Total Sugars ${fmt(n.sugars_g)}</span></div>
          <div class="nf-row indent2"><span>Includes ${fmt(n.added_sugars_g)} Added Sugars</span><b>${dv.added_sugars_g}%</b></div>
          <div class="nf-rule-mid"></div>
          <div class="nf-row"><span><b>Protein</b> ${fmt(n.protein_g)}</span></div>
          <div class="nf-rule-thin2"></div>
          <div class="nf-micro"><span>Vitamin D ${n.vitamin_d_mcg}mcg</span><span>${dv.vitamin_d_mcg}%</span></div>
          <div class="nf-micro"><span>Calcium ${n.calcium_mg}mg</span><span>${dv.calcium_mg}%</span></div>
          <div class="nf-micro"><span>Iron ${fmt(n.iron_mg, "mg")}</span><span>${dv.iron_mg}%</span></div>
          <div class="nf-micro last"><span>Potassium ${n.potassium_mg}mg</span><span>${dv.potassium_mg}%</span></div>
          <div class="nf-foot">* The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to a daily diet. 2,000 calories a day is used for general nutrition advice.</div>
        </div>
        <div class="ingbox">
          <div><b>${safeStatement}</b></div>
          ${safeContains ? `<div class="contains"><b>${safeContains}</b></div>` : ""}
          ${addressBlock ? `<div class="address">${addressBlock}</div>` : ""}
        </div>
        <div class="disclaimer">Nutrition values calculated from ingredient database values using FDA rounding rules (21 CFR 101.9). Final label review is the responsibility of the manufacturer.</div>
      </div>`;

    // FDA linear (small-package) format for bottle/jar wrap labels —
    // 21 CFR 101.9(j)(13) permits this condensed layout when label space is limited.
    const linearNF =
      `<b>Nutrition Facts</b> Serv. size: ${esc(servingSizeDisplay)}, ` +
      `${esc(servingCountDisplay)}, <b>Amount per serving: Calories ${n.calories},</b> ` +
      `Total Fat ${fmt(n.total_fat_g)} (${dv.total_fat_g}% DV), Sat. Fat ${fmt(n.saturated_fat_g)} (${dv.saturated_fat_g}% DV), ` +
      `<i>Trans</i> Fat ${fmt(n.trans_fat_g)}, Cholest. ${fmt(n.cholesterol_mg, "mg")} (${dv.cholesterol_mg}% DV), ` +
      `Sodium ${fmt(n.sodium_mg, "mg")} (${dv.sodium_mg}% DV), Total Carb. ${fmt(n.total_carbs_g)} (${dv.total_carbs_g}% DV), ` +
      `Fiber ${fmt(n.fiber_g)} (${dv.fiber_g}% DV), Total Sugars ${fmt(n.sugars_g)} (Incl. ${fmt(n.added_sugars_g)} Added Sugars, ${dv.added_sugars_g}% DV), ` +
      `Protein ${fmt(n.protein_g)}, Vit. D ${n.vitamin_d_mcg}mcg (${dv.vitamin_d_mcg}% DV), Calcium ${n.calcium_mg}mg (${dv.calcium_mg}% DV), ` +
      `Iron ${fmt(n.iron_mg, "mg")} (${dv.iron_mg}% DV), Potas. ${n.potassium_mg}mg (${dv.potassium_mg}% DV). ` +
      `% DV = % Daily Value based on a 2,000 calorie diet.`;

    const wrapHTML = `
      ${stampHTML}
      <div class="wrap">
        <div class="panel brandcol">
          ${logo ? `<img class="logo" src="${logo}" alt=""/>` : ""}
          ${brand ? `<div class="brand">${brand}</div>` : ""}
          ${safeProductName ? `<div class="product">${safeProductName}</div>` : ""}
          <div class="netwt">NET WT ${safeNetWeight}</div>
        </div>
        <div class="panel infocol">
          <div class="linear-nf">${linearNF}</div>
          <div class="stmt"><b>${safeStatement}</b></div>
          ${safeContains ? `<div class="stmt"><b>${safeContains}</b></div>` : ""}
          ${addressBlock ? `<div class="address">${addressBlock}</div>` : ""}
        </div>
      </div>`;

    const f = preset.fs || 1;
    const wrapStyles = `
      @page { size: ${preset.w}in ${preset.h}in; margin: 0; }
      * { box-sizing: border-box; margin: 0; }
      body { width: ${preset.w}in; height: ${preset.h}in; background: white; color: black;
             font-family: Helvetica, Arial, sans-serif; overflow: hidden; }
      .wrap { display: flex; width: 100%; height: 100%; border: 0.5pt solid #bbb; }
      .panel { padding: 0.08in; }
      .brandcol { width: 34%; display: flex; flex-direction: column; align-items: center; justify-content: center;
                  text-align: center; border-right: 0.75pt solid black; }
      .logo { max-width: 100%; max-height: ${0.75*Math.min(f,1.3)}in; object-fit: contain; margin-bottom: 0.04in; }
      .brand { font-size: ${10*f}pt; font-weight: 900; letter-spacing: -0.02em; line-height: 1.05; }
      .product { font-size: ${8*f}pt; font-weight: 700; margin-top: 0.02in; line-height: 1.1; }
      .netwt { font-size: ${6*f}pt; margin-top: 0.04in; }
      .infocol { width: 66%; display: flex; flex-direction: column; justify-content: center; gap: 0.035in; }
      .linear-nf { font-size: ${6*f}pt; line-height: 1.25; }
      .stmt { font-size: ${6*f}pt; line-height: 1.25; }
      .address { font-size: ${5.5*f}pt; line-height: 1.25; border-top: 0.5pt solid black; padding-top: 0.03in; }
      .stamp { position: absolute; top: 0.02in; left: 0; right: 0; text-align: center;
               font-size: ${5*f}pt; font-weight: 900; letter-spacing: 0.15em; color: #b00; }
    `;

    const docHTML = preset.wrap ? wrapHTML : labelHTML;

    const styles = preset.wrap ? wrapStyles : `
      @page { ${preset.w ? `size: ${preset.w}in ${preset.h}in; margin: 0.2in;` : "margin: 0.4in; size: auto;"} }
      .stamp { text-align: center; font-size: 10px; font-weight: 900; letter-spacing: 0.15em; color: #b00; margin-bottom: 4px; }
      * { box-sizing: border-box; }
      body { margin: 0; padding: 24px; background: white; color: black; font-family: Helvetica, Arial, sans-serif; }
      .label { max-width: 340px; }
      .header { border: 3px solid black; border-bottom: 0; text-align: center; padding: 6px 8px 4px; }
      .header .logo { max-width: 120px; max-height: 60px; object-fit: contain; margin-bottom: 4px; }
      .brand { font-size: 15px; font-weight: 900; letter-spacing: -0.02em; }
      .product { font-size: 13px; font-weight: 700; }
      .netwt { font-size: 10px; margin-top: 2px; }
      .nutbox { border: 3px solid black; padding: 8px; }
      .nf-title { font-size: 28px; font-weight: 900; line-height: 1; letter-spacing: -0.02em; }
      .nf-spc { font-size: 11px; }
      .nf-rule-thin { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 8px solid black; padding-bottom: 1px; font-size: 13px; font-weight: bold; }
      .nf-amount { font-size: 10px; margin-top: 4px; }
      .nf-cal-row { display:flex; justify-content:space-between; align-items:flex-end; border-bottom: 4px solid black; padding-bottom: 1px; font-weight: 900; font-size: 22px; }
      .nf-cal-row .cal { font-size: 24px; }
      .nf-dv { text-align: right; font-size: 10px; font-weight: bold; margin-top: 2px; }
      .nf-row { display:flex; justify-content:space-between; border-bottom: 1px solid black; padding: 1px 0; font-size: 13px; }
      .nf-row.indent { padding-left: 16px; }
      .nf-row.indent2 { padding-left: 32px; }
      .nf-rule-mid { border-bottom: 8px solid black; }
      .nf-rule-thin2 { border-bottom: 3px solid black; margin-top: 1px; }
      .nf-micro { display:flex; justify-content:space-between; border-bottom: 1px solid black; padding: 1px 0; font-size: 12px; }
      .nf-micro.last { border-bottom: 0; }
      .nf-foot { font-size: 9px; line-height: 1.2; margin-top: 8px; }
      .ingbox { border: 3px solid black; border-top: 0; padding: 6px 8px; font-size: 9px; line-height: 1.3; }
      .ingbox .contains { margin-top: 4px; }
      .ingbox .address { margin-top: 6px; padding-top: 6px; border-top: 1px solid black; font-size: 8.5px; }
      .disclaimer { font-size: 8px; color: #555; margin-top: 6px; max-width: 340px; line-height: 1.3; }
    `;

    // Use a hidden iframe — no popup blocker, no async CSS loading
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
    document.body.appendChild(iframe);
    const idoc = iframe.contentDocument || iframe.contentWindow.document;
    idoc.open();
    idoc.write(`<!DOCTYPE html><html><head>
      <meta charset="utf-8"/><title>${safeProductName || "Label"}</title>
      <style>${styles}</style>
    </head><body>${docHTML}</body></html>`);
    idoc.close();
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (err) {
        console.error("Iframe print failed, falling back to window.print", err);
        window.print();
      }
      setTimeout(() => {
        try { document.body.removeChild(iframe); }
        catch (err) { console.error("Print iframe cleanup failed", err); }
      }, 2000);
    }, 400);
  };

  const duplicateAsNewVersion = async () => {
    if (draft.dirty) { toast.error("Save your edits before creating a new version."); return; }
    if (!id) return toast.error("Save first, then duplicate");
    try {
      const { data } = await api.post(`/recipes/${id}/duplicate`);
      toast.success(`Created ${data.version}`);
      navigate(`/builder/${data.id}`);
    } catch (e) { toast.error("Duplicate failed"); }
  };

  const refreshFromStarter = async () => {
    if (draft.dirty) { toast.error("Save your edits before refreshing from a starter."); return; }
    if (!id) return;
    if (!confirm(
      "Refresh this formulation with the latest starter template?\n\n" +
      "This will REPLACE the ingredient list, production directions, and notes " +
      "with the latest version. Your name, SKU, version, status, and cost settings " +
      "stay the same.\n\nA backup snapshot is saved automatically before changes."
    )) return;
    try {
      const { data } = await api.post(`/recipes/${id}/refresh-from-starter`);
      toast.success(`Refreshed from "${data.starter_name}" — ${data.ingredient_count} ingredients`);
      // Reload from server so the new ingredient list + directions render
      window.location.reload();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          || "Refresh failed — this formulation may not be linked to a starter template."
      );
    }
  };

  if (!loaded) return <Layout><div className="max-w-4xl mx-auto p-8" role="status">
    {loadError || "Loading your saved formula before enabling edits…"}
    {loadError && <Button className="ml-3" onClick={() => window.location.reload()}>Retry</Button>}
  </div></Layout>;

  return (
    <SectionExportContext.Provider value={{ title: productName || name || "Untitled formula", disabled: isDemo }}>
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col mb-6 gap-5 min-w-0" data-testid="recipe-header">
          <div className="w-full min-w-0">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Builder</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Formulation name (internal)"
              className="text-3xl font-bold tracking-tight border-0 border-b border-stone-200 rounded-none px-0 focus-visible:ring-0 focus-visible:border-brand-500 mt-2"
              data-testid="recipe-name-input" />
            <Input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Product name (shown on label)"
              className="text-lg text-stone-600 border-0 rounded-none px-0 focus-visible:ring-0 mt-1"
              data-testid="product-name-input" />
            <div className="flex flex-wrap gap-3 mt-3">
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider text-stone-500">SKU</Label>
                <Input value={sku} onChange={(e) => setSku(e.target.value)}
                  placeholder="e.g., ROMG-001" className="h-8 w-36" data-testid="sku-input" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Version</Label>
                <Input value={version} onChange={(e) => setVersion(e.target.value)}
                  placeholder="v1" className="h-8 w-20" data-testid="version-input" />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Type</Label>
                <div className="flex flex-col gap-1 min-w-0 max-w-full sm:max-w-[300px]">
                  <Select value={productType} onValueChange={setProductType}>
                    <SelectTrigger className="h-8 w-44 sm:w-48 max-w-full" data-testid="product-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(PRODUCT_TYPES).map(([key, def]) => (
                        <SelectItem key={key} value={key}>{def.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {productType === "beverage" && (
                    <div className="text-[10px] text-amber-700 bg-amber-50 p-1.5 rounded border border-amber-200">
                      <b>Advisory:</b> Document the production method and complete the distribution/acidification safety inputs. This tool does not imply shelf stability or process validation for beverages.
                    </div>
                  )}
                  {productType === "baked_goods" && (
                    <div className="text-[10px] text-orange-700 bg-orange-50 p-1.5 rounded border border-orange-200">
                      <b>Advisory:</b> Note flour-relative percentages, complete allergen review, evaluate moisture/texture, perform shelf-life testing, and check actual post-bake yield.
                    </div>
                  )}
                  {productType === "dairy_plant" && (
                    <>
                      <Select value={productForm || "unspecified"} onValueChange={(value) => setProductForm(value === "unspecified" ? "" : value)}>
                        <SelectTrigger className="h-8 w-44 sm:w-48 max-w-full" data-testid="product-form-select">
                          <SelectValue placeholder="Choose physical form" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unspecified">Choose physical form</SelectItem>
                          <SelectItem value="liquid">Liquid / pourable</SelectItem>
                          <SelectItem value="spoonable">Spoonable / semi-solid</SelectItem>
                          <SelectItem value="solid">Solid</SelectItem>
                          <SelectItem value="powder">Powder</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="text-[10px] text-sky-700 bg-sky-50 p-1.5 rounded border border-sky-200">
                        <b>Advisory:</b> Choose the physical form so liquid products receive volume-based serving and net-content helpers. Safety guidance remains product-specific; this is not a fermentation, aseptic, or process-validation module.
                      </div>
                    </>
                  )}
                </div>
              </div>
              {productType === "pet_topper" && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Species</Label>
                  <Select value={species} onValueChange={setSpecies}>
                    <SelectTrigger className="h-8 w-24" data-testid="species-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dog">Dog</SelectItem>
                      <SelectItem value="cat">Cat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Label className="text-xs uppercase tracking-wider text-stone-500">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-8 w-32" data-testid="status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="R&D">R&amp;D</SelectItem>
                    <SelectItem value="Pilot">Pilot</SelectItem>
                    <SelectItem value="Production">Production</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1 bg-stone-100 rounded-md p-0.5" data-testid="mode-toggle">
                <button onClick={() => setMode("retail")}
                  className={`px-2.5 py-1 text-xs rounded transition-colors flex items-center gap-1 ${mode === "retail" ? "bg-white shadow-sm text-brand-800 font-semibold" : "text-stone-500 hover:text-stone-700"}`}
                  data-testid="mode-retail-btn">
                  <Storefront size={12} weight="bold" /> Retail
                </button>
                <button onClick={() => setMode("chef")}
                  className={`px-2.5 py-1 text-xs rounded transition-colors flex items-center gap-1 ${mode === "chef" ? "bg-white shadow-sm text-brand-800 font-semibold" : "text-stone-500 hover:text-stone-700"}`}
                  data-testid="mode-chef-btn">
                  <ChefHat size={12} weight="bold" /> Chef
                </button>
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap w-full min-w-0 border-t border-stone-200 pt-4 [&>button]:max-w-full [&>button]:whitespace-normal [&>button]:h-auto [&>button]:min-h-10 [&>button]:py-2" data-testid="recipe-actions">
            {id && !isDemo && <Button variant="outline" onClick={() => {
              if (!draft.dirty || window.confirm("Open R&D workspace? Unsaved edits remain in this browser's recovery draft.")) navigate(`/rd/${id}`);
            }} data-testid="rd-workspace-button">R&amp;D workspace &amp; history</Button>}
            <Button variant="outline" onClick={() => {
              setShowApplicationCalcs(true);
              document.querySelector('[data-testid="application-calculators-card"]')?.scrollIntoView({behavior:"smooth",block:"start"});
            }}>All application calculators</Button>
            {id && clonedFromStarter && (
              <Button variant="outline" onClick={refreshFromStarter}
                className="border-brand-300 text-brand-800 hover:bg-brand-50"
                data-testid="refresh-from-starter-button"
                title="Pull in the latest ingredients & directions from the starter template this formulation was cloned from">
                🔄 Refresh from starter
              </Button>
            )}
            {id && (
              <Button variant="outline" onClick={duplicateAsNewVersion} className="border-stone-300"
                data-testid="duplicate-version-button">
                <CopySimple size={16} className="mr-2" /> New version
              </Button>
            )}
            {!isDemo && (
              <Button variant="outline" onClick={printFormula} data-testid="print-button" className="border-stone-300">
                <Printer size={16} className="mr-2" /> Print formula
              </Button>
            )}
            {!isDemo && !isPet && (
              <Button variant="outline" onClick={() => print(labelPreset, labelPurpose)} data-testid="print-label-button" className="border-stone-300"
                title="Print-ready label: Nutrition Facts panel + ingredient statement + Contains line + responsible-party address. Use your browser's Save as PDF to send to a label printer.">
                <Printer size={16} className="mr-2" /> Generate label
              </Button>
            )}
            {id && !isDemo && (
              <Button variant="outline" onClick={generateCommercializationPackage}
                className="border-brand-300 text-brand-800 hover:bg-brand-50"
                data-testid="commercialization-package-button"
                title="One PDF bundling formula, batch scaling, ingredient statement, Nutrition Facts, label, allergens, processing, packaging, spec, and version history — the co-manufacturer handoff document. Save as PDF from the print dialog.">
                <Files size={16} className="mr-2" /> Commercialization package
              </Button>
            )}
            {id && !isDemo && (
              <Button variant="outline" onClick={shareSpecSheet}
                className="border-brand-300 text-brand-800 hover:bg-brand-50"
                data-testid="share-spec-sheet-button"
                title="Generate a public link to this formulation's spec sheet — read-only, safe to forward to a buyer or co-packer.">
                <ShareNetwork size={16} className="mr-2" /> Share spec sheet
              </Button>
            )}
            {!isDemo && (
              <Button onClick={() => save()} disabled={saving || !loaded || !!draft.pending} className="bg-brand-800 hover:bg-brand-700 text-white"
                data-testid="save-recipe-button">
                <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save"}
              </Button>
            )}
          </div>
        </div>

        {!isDemo && <div role="status" className="mb-4 text-sm" data-testid="formula-save-status">
          {loadError || (!loaded ? "Loading saved formula…" : draft.dirty ? "Unsaved changes — recovery draft stored on this browser." : "No unsaved changes.")}
          {draft.storageError && <p role="alert" className="text-red-800">{draft.storageError}</p>}
          <p className="text-xs text-stone-500">Browser drafts are not server saves. Use Save to create a recoverable formula revision.</p>
        </div>}
        {draft.pending && <div role="alert" className="mb-4 border border-amber-300 bg-amber-50 rounded p-4">
          <b>Unsaved browser draft found.</b> Recover it before continuing, or discard only this local draft.
          <div className="flex gap-3 mt-3"><Button onClick={draft.recover}>Recover draft</Button>
          <Button variant="outline" onClick={() => {if(window.confirm("Discard the unsaved browser draft? The saved formula is unchanged.")) draft.discard();}}>Discard draft</Button></div>
        </div>}
        {saveError && (
          <div role="alert" className="rounded-md border border-red-300 bg-red-50 text-red-900 p-3 mb-4 text-sm" data-testid="save-recipe-error">
            <b>Formulation not saved.</b> {saveError}
            {id && <Button variant="outline" onClick={() => save(true)} disabled={saving} className="ml-3">Save edits as a separate formula</Button>}
          </div>
        )}
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 mb-6 flex gap-3 text-sm" data-testid="verify-banner">
          <Warning size={18} weight="fill" className="shrink-0 mt-0.5" />
          <div>
            <b>Calculated values only.</b> Verify with laboratory analysis before commercial distribution.
            Alaine's Bench is a tool; you are solely responsible for the accuracy of your final label.
          </div>
        </div>

        <div className="grid xl:grid-cols-[minmax(0,1fr)_320px] gap-6 min-w-0">
          {/* LEFT: Builder */}
          <div className="space-y-5 min-w-0">
            <Card className="p-5 border-stone-200">
              <div className="grid sm:grid-cols-3 gap-4">
                <LabelField label={isLiquidProduct ? "Serving size (g + mL)" : "Serving size (g)"}>
                  <Input type="number" min="0.01" step="0.01" value={servingSize}
                    onChange={(e) => setServingSize(e.target.value)} data-testid="serving-size-input"/>
                  {isLiquidProduct && Number(servingSize) > 0 && (
                    <div className="text-[11px] text-sky-700 mt-1 font-mono" data-testid="serving-size-volume">
                      ≈ {gramsToMl(servingSize).toFixed(0)} mL · {gramsToFlOz(servingSize).toFixed(2)} fl oz
                      <div className="text-[10px] text-stone-500 mt-0.5">FDA 21 CFR 101.9 — both required for liquids</div>
                    </div>
                  )}
                </LabelField>
                <LabelField label="Servings per container"><Input type="text" value={servingCount ? `${servingCount.about ? "about " : ""}${servingCount.value}` : ""}
                  readOnly placeholder="Enter package weight" data-testid="servings-per-input"/>
                  <p className="text-xs text-stone-500 mt-1">{servingCountDisplay}</p>
                </LabelField>
                <LabelField label="Target batch size">
                  <Select value={String(batchSizeG)} onValueChange={(v) => scaleBatchTo(Number(v))}>
                    <SelectTrigger data-testid="batch-size-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Lab / R&D scale</div>
                      <SelectItem value="10">10 g (sensory pinch)</SelectItem>
                      <SelectItem value="25">25 g (taste panel)</SelectItem>
                      <SelectItem value="50">50 g (small lab batch)</SelectItem>
                      <SelectItem value="100">100 g</SelectItem>
                      <SelectItem value="250">250 g</SelectItem>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-1">Pilot / kitchen scale</div>
                      <SelectItem value="500">500 g</SelectItem>
                      <SelectItem value="1000">1 kg (1000 g)</SelectItem>
                      <SelectItem value="453.592">1 lb (453.6 g)</SelectItem>
                      <SelectItem value="907.185">2 lb (907.2 g)</SelectItem>
                      <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-stone-500 font-semibold mt-1">Production scale</div>
                      <SelectItem value="2267.96">5 lb (2,268 g)</SelectItem>
                      <SelectItem value="4535.92">10 lb (4,536 g)</SelectItem>
                      <SelectItem value="11339.8">25 lb (11,340 g)</SelectItem>
                      <SelectItem value="22679.6">50 lb (22,680 g)</SelectItem>
                    </SelectContent>
                  </Select>
                </LabelField>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <LabelField label="Household serving measure">
                  <Input value={servingMeasure} maxLength={120} onChange={(e) => setServingMeasure(e.target.value)}
                    placeholder="e.g. 1/4 tsp, 2 tbsp, 1 piece" data-testid="serving-measure-input" />
                </LabelField>
                <LabelField label="Net weight of ONE container (g)">
                  <Input type="number" min="0.01" step="0.01" value={unitSizeG}
                    onChange={(e) => { setUnitSizeG(e.target.value); setNetWeight(e.target.value ? `${e.target.value} g` : ""); }} placeholder="e.g. 113"
                    data-testid="serving-container-weight-input" />
                </LabelField>
              </div>
              <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-900" data-testid="serving-guidance">
                Serving size must match the applicable FDA food category and the measured weight of your household measure.
                Spices/herbs use 1/4 tsp; weigh that amount for this specific blend. The 0.5 g alternative applies only when not measurable by teaspoon.
                Other foods, concentrates, prepared products and pet products need their own basis—do not apply a spice serving to every formula.
                {!servingMeasure.trim() && <p className="font-semibold mt-1">Household measure not confirmed. Existing formula weights are not automatically verified.</p>}
                {!servingCount && <p className="font-semibold mt-1">Enter one container’s net weight to verify its serving count. Batch weight is not package weight.</p>}
                {servingCount && servingCount.raw < 3 && <p className="font-semibold mt-1">Small package: review single-serving and dual-column requirements before commercial labeling; this count is arithmetic only.</p>}
                <a href="https://www.ecfr.gov/current/title-21/section-101.12" target="_blank" rel="noreferrer" className="underline block mt-1">FDA reference amounts (21 CFR 101.12)</a>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-3 items-end">
                <LabelField label="Custom batch size (grams)">
                  <Input type="number" step="0.1" min="0.1" placeholder="e.g. 17.5"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        const v = Number(e.target.value);
                        if (v > 0) {
                          scaleBatchTo(v);
                          e.target.value = "";
                        }
                      }
                    }}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v > 0) {
                        scaleBatchTo(v);
                        e.target.value = "";
                      }
                    }}
                    data-testid="custom-batch-size-input"
                  />
                </LabelField>
                <div className="text-[11px] text-stone-500 leading-tight">
                  Type any gram value and press Enter. The whole formula scales proportionally —
                  ratios stay identical, perfect for sensory panels, lab work, or scale-up trials.
                </div>
              </div>
              <div className="mt-4">
                <Label className="text-xs uppercase tracking-wider text-stone-500">
                  Net weight (shown on front panel)
                </Label>
                <Input value={netWeight} onChange={(e) => { setNetWeight(e.target.value); setUnitSizeG(packageMass(e.target.value) || ""); }}
                  placeholder={isLiquidProduct ? "16 fl oz (473 mL)" : "4 oz (113g)"}
                  className="mt-1" data-testid="net-weight-input" />
                {isLiquidProduct && Number(unitSizeG) > 0 && (
                  <div className="text-[11px] text-sky-700 mt-1 font-mono flex items-center gap-2"
                    data-testid="net-weight-volume-hint">
                    <span>
                      Suggested: <b>{gramsToFlOz(unitSizeG).toFixed(1)} fl oz ({gramsToMl(unitSizeG).toFixed(0)} mL)</b>
                    </span>
                    <button type="button"
                      onClick={() => setNetWeight(`${gramsToFlOz(unitSizeG).toFixed(1)} fl oz (${gramsToMl(unitSizeG).toFixed(0)} mL)`)}
                      className="text-brand-700 underline text-[11px]"
                      data-testid="apply-net-weight-suggestion">
                      apply
                    </button>
                  </div>
                )}
                {isLiquidProduct && (
                  <div className="text-[10px] text-stone-500 mt-1 leading-snug">
                    FDA requires <b>fluid ounces</b> (not weight oz) for liquid products on the principal display panel —
                    <i> 21 CFR 101.105</i>.
                  </div>
                )}
              </div>
              <div className="text-xs text-stone-500 mt-2 flex items-center justify-between flex-wrap gap-2">
                <span>
                  Actual total: <span className="font-semibold text-stone-700">{totalG.toFixed(1)}g</span>
                  {" · "}{(totalG / 28.3495).toFixed(2)} oz · {(totalG / 453.592).toFixed(3)} lb
                  {isLiquidProduct && totalG > 0 && (
                    <span className="text-sky-700 ml-2" data-testid="batch-volume">
                      · {gramsToMl(totalG).toFixed(0)} mL · {gramsToFlOz(totalG).toFixed(1)} fl oz
                      <span className="text-stone-400"> (avg density {avgDensity.toFixed(2)} g/mL)</span>
                    </span>
                  )}
                </span>
                {Math.abs(totalG - batchSizeG) > 0.5 && totalG > 0 && (
                  <button onClick={() => scaleBatchTo(batchSizeG)} className="text-brand-700 underline text-xs"
                    data-testid="rescale-button">Rescale to {batchSizeG}g</button>
                )}
              </div>
            </Card>

            <Card className="p-5 border-stone-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold">Formulation table</h3>
              </div>
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" size={16} />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search ingredients to add…"
                  className="pl-9 border-stone-300 focus-visible:ring-brand-500" data-testid="ingredient-search-input"/>
                {found.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-64 overflow-auto">
                    {found.map((f) => (
                      <button key={f.id} onClick={() => add(f)} data-testid={`add-${f.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center justify-between text-sm">
                        <span>{f.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-stone-500">{f.category.replace("_", " ")}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-500">
                      <th className="py-2">Ingredient</th>
                      <th className="py-2 w-28">Weight (g)</th>
                      <th className="py-2 w-20 text-right">lb</th>
                      <th className="py-2 w-24 text-right text-brand-700 font-semibold">% of Formula</th>
                      <th className="py-2 w-24 text-right">Cost</th>
                      <th className="py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {rows.map((r, i) => {
                        const grams = Number(r.grams) || 0;
                        const pct = totalG ? ((grams / totalG) * 100) : 0;
                        const lb = grams / 453.592;
                        const rowCost = costByIngredient[r.ingredient_id] || 0;
                        const hasPrice = Number(r._price) > 0;
                        return (
                          <motion.tr key={r.ingredient_id} layout
                            initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className="border-b border-stone-100" data-testid={`row-${r.ingredient_id}`}>
                            <td className="py-2">
                              <div className="flex items-center gap-1">
                                <button type="button" onClick={() => openIntel(r.ingredient_id, r._display)}
                                  className="text-stone-400 hover:text-brand-700 transition-colors"
                                  title="Ingredient intelligence"
                                  data-testid={`intel-${r.ingredient_id}`}>
                                  <Info size={14} weight="bold" />
                                </button>
                                <span>{r._display}</span>
                                {r._is_liquid && (
                                  <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-mono uppercase bg-sky-100 text-sky-800 border border-sky-200" title="Liquid ingredient — gram amount = volume × density">
                                    Liquid
                                  </span>
                                )}
                              </div>
                              {r._role && (
                                <div className="mt-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] bg-violet-50 text-violet-700 border border-violet-200"
                                  title="Functional role — what this ingredient does in the formula"
                                  data-testid={`role-${r.ingredient_id}`}>
                                  {r._role}
                                </div>
                              )}
                              <div className="mt-1">
                                <RecipeRowLotPicker
                                  ingredientId={r.ingredient_id}
                                  ingredientName={r._display}
                                  value={{ supplier_spec_id: r.supplier_spec_id, lot_id: r.lot_id, lot_number: r.lot_number }}
                                  onChange={(next) => setRows((curr) => curr.map((row, idx) =>
                                    idx === i ? { ...row, ...next } : row
                                  ))}
                                />
                              </div>
                            </td>
                            <td className="py-2">
                              <Input type="number" step="0.01" value={r.grams} onChange={(e) => setGrams(i, e.target.value)}
                                className="h-8 border-stone-300 focus-visible:ring-brand-500"
                                data-testid={`grams-${r.ingredient_id}`}/>
                              {r._is_liquid && r._density > 0 && Number(r.grams) > 0 && (
                                <div className="text-[10px] text-sky-700 mt-0.5 font-mono">
                                  ≈ {(Number(r.grams) / r._density).toFixed(1)} mL
                                </div>
                              )}
                            </td>
                            <td className="py-2 text-right font-mono text-stone-500 text-xs">{lb.toFixed(4)}</td>
                            <td className="py-2 text-right font-mono text-brand-800 font-semibold">{pct.toFixed(2)}%</td>
                            <td className="py-2 text-right font-mono text-xs">
                              {hasPrice
                                ? <span className="text-stone-700">${rowCost.toFixed(4)}</span>
                                : <span className="text-amber-600" title="Set $/lb on Ingredients page">—</span>}
                            </td>
                            <td className="py-2 text-right">
                              <Button variant="ghost" size="sm" onClick={() => remove(i)} data-testid={`remove-${r.ingredient_id}`}>
                                <Trash size={14} className="text-red-600" />
                              </Button>
                            </td>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                    {rows.length === 0 && (
                      <tr><td colSpan={6} className="py-8 text-center text-stone-400 text-sm">Search above to add your first ingredient.</td></tr>
                    )}
                    {rows.length > 0 && (
                      <tr className="border-t-2 border-brand-700 font-bold text-stone-800 bg-brand-50/50">
                        <td className="py-3">TOTAL FORMULA</td>
                        <td className="py-3 text-sm">{totalG.toFixed(1)} g</td>
                        <td className="py-3 text-right font-mono text-xs">{(totalG / 453.592).toFixed(4)} lb</td>
                        <td className="py-3 text-right font-mono text-brand-800 text-base">
                          {rows.reduce((a, r) => a + (totalG ? (Number(r.grams) / totalG) * 100 : 0), 0).toFixed(2)}%
                        </td>
                        <td className="py-3 text-right font-mono text-sm">${costs.batchCost.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>

            {calc?.statement && (
              <Card className="p-5 border-stone-200">
                <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Ingredient Statement</div>
                <div className="font-mono text-sm mt-2 text-stone-800 leading-relaxed" data-testid="ingredient-statement">
                  {calc.statement.statement}
                </div>
                {calc.statement.contains_line && (
                  <div className="font-mono text-sm mt-3 font-bold text-red-800" data-testid="contains-line">
                    {calc.statement.contains_line}
                  </div>
                )}
              </Card>
            )}

            {/* Formulation Flags (industry limits, mode-aware). Always visible so users see active mode + limits. */}
            {calc?.formulation_warnings && rows.length > 0 && (() => {
              const activeWarnings = calc.formulation_warnings[mode] || [];
              const otherMode = mode === "retail" ? "chef" : "retail";
              const otherCount = (calc.formulation_warnings[otherMode] || []).length;
              return (
                <Card className="p-5 border-stone-200" data-testid="warnings-panel">
                  <div className="flex items-center gap-2 mb-3">
                    <Warning size={18} weight="fill" className={activeWarnings.length > 0 ? "text-amber-600" : "text-emerald-600"} />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                        Formulation flags · <b className="text-brand-800">{mode} mode</b>
                      </div>
                      <h3 className="font-semibold mt-0.5">
                        {activeWarnings.length > 0
                          ? `${activeWarnings.length} guard-rail${activeWarnings.length > 1 ? "s" : ""} active`
                          : "All ingredients within typical " + mode + " limits ✓"}
                      </h3>
                    </div>
                    {otherCount !== activeWarnings.length && (
                      <button onClick={() => setMode(otherMode)}
                        className="text-[11px] px-2 py-1 rounded border border-stone-300 hover:border-brand-700 hover:text-brand-800 text-stone-600 transition-colors"
                        data-testid="switch-mode-link">
                        Try {otherMode} mode ({otherCount} flag{otherCount !== 1 ? "s" : ""})
                      </button>
                    )}
                  </div>

                  {activeWarnings.length > 0 ? (
                    <div className="space-y-2">
                      {activeWarnings.map((w, i) => {
                        const key = `${w.ingredient}-${w.pct}`;
                        const fix = fixSuggestions[key];
                        return (
                          <div key={`${w.ingredient}-${i}`}
                            className={`rounded-md border ${
                              w.level === "warning" ? "bg-red-50 border-red-200 text-red-900" :
                              w.level === "caution" ? "bg-amber-50 border-amber-200 text-amber-900" :
                              "bg-blue-50 border-blue-200 text-blue-900"
                            }`}
                            data-testid={`warning-${w.ingredient.replace(/[^a-z0-9]/gi, "-")}`}>
                            <div className="px-3 py-2 text-sm flex gap-3 items-start">
                              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                              <div className="flex-1 min-w-0">
                                <div className="font-semibold">
                                  {w.ingredient} at <span className="font-mono">{w.pct}%</span>
                                  <span className="text-xs font-normal opacity-75 ml-2">(typical {mode} limit: {w.limit}%)</span>
                                </div>
                                <div className="text-xs mt-0.5 opacity-90">{w.reason}</div>
                              </div>
                              <button onClick={() => requestFix(w)}
                                disabled={fix?.loading || !!fix?.text}
                                className="text-[11px] shrink-0 px-2 py-1 rounded bg-white/70 hover:bg-white border border-current/30 font-medium disabled:opacity-50"
                                data-testid={`suggest-fix-${w.ingredient.replace(/[^a-z0-9]/gi, "-")}`}>
                                {fix?.loading ? "Thinking…" : fix?.text ? "✓ Suggested" : "Suggest a fix"}
                              </button>
                            </div>
                            {fix?.text && (
                              <div className="px-3 pb-3 pt-1 border-t border-current/10">
                                <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold mb-1.5">R&amp;D Suggestion</div>
                                <div className="text-sm leading-relaxed whitespace-pre-wrap font-normal opacity-95"
                                  data-testid={`fix-result-${w.ingredient.replace(/[^a-z0-9]/gi, "-")}`}>
                                  {fix.text}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900">
                      Every ingredient sits within the typical {mode}-mode usage range encoded from
                      Spice House Pro Council, FEMA GRAS, ASTA, and 21 CFR. Trust your palate from here.
                    </div>
                  )}
                  <div className="text-[11px] text-stone-500 mt-3 leading-relaxed">
                    <b>Retail</b> = tighter limits for shelf-stable consumer brands. <b>Chef</b> = wider limits
                    for restaurant / private-label / culinary blends. Toggle at top of page.
                  </div>
                </Card>
              );
            })()}

            {/* Common-name validator (21 CFR 102.5) */}
            {calc?.common_name_check && rows.length > 0 && (() => {
              const cn = calc.common_name_check;
              const tone =
                cn.status === "match"   ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", iconColor: "text-emerald-700", label: "Common name verified" } :
                cn.status === "partial" ? { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   iconColor: "text-amber-700",   label: "Partial common-name match" } :
                                          { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-900",     iconColor: "text-red-700",     label: "Common-name mismatch" };
              return (
                <Card className="p-5 border-stone-200" data-testid="common-name-card">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={18} weight="duotone" className={tone.iconColor} />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                        Common name · <b className="text-brand-800">21 CFR 102.5</b>
                      </div>
                      <h3 className="font-semibold mt-0.5">
                        {tone.label}: <span className="text-brand-800">{cn.category}</span>
                      </h3>
                    </div>
                  </div>
                  <div className={`rounded-md ${tone.bg} ${tone.border} border ${tone.text} px-3 py-2.5 text-sm leading-relaxed`}>
                    <div>
                      Detected <b>{cn.found_count} of {cn.expected_count}</b> expected core ingredients
                      ({cn.min_match} required for category match).
                    </div>
                    {cn.found.length > 0 && (
                      <div className="mt-1.5 text-[12px]">
                        <b>Found:</b> {cn.found.join(", ")}
                      </div>
                    )}
                    {cn.missing.length > 0 && (
                      <div className="mt-1 text-[12px]">
                        <b>Missing (typical):</b> {cn.missing.join(", ")}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-600 mt-2.5 leading-relaxed bg-stone-50 border border-stone-200 rounded px-3 py-2">
                    <b>Note:</b> {cn.note}
                  </div>
                  <div className="text-[10px] text-stone-400 mt-2">{cn.citation}</div>
                </Card>
              );
            })()}

            {/* Mesh-size compatibility (segregation risk) */}
            {calc?.mesh_check && rows.length > 0 && (() => {
              const m = calc.mesh_check;
              const tone =
                m.status === "ok"      ? { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", iconColor: "text-emerald-700" } :
                m.status === "caution" ? { bg: "bg-amber-50",   border: "border-amber-200",   text: "text-amber-900",   iconColor: "text-amber-700"   } :
                                         { bg: "bg-red-50",     border: "border-red-200",     text: "text-red-900",     iconColor: "text-red-700"     };
              return (
                <Card className="p-5 border-stone-200" data-testid="mesh-check-card">
                  <div className="flex items-center gap-2 mb-2">
                    <Warning size={18} weight="duotone" className={tone.iconColor} />
                    <div className="flex-1">
                      <div className="text-xs uppercase tracking-[0.2em] text-stone-500">
                        Particle-size compatibility · <b className="text-brand-800">segregation check</b>
                      </div>
                      <h3 className="font-semibold mt-0.5">{m.message}</h3>
                    </div>
                  </div>
                  <div className={`rounded-md ${tone.bg} ${tone.border} border ${tone.text} px-3 py-2.5 text-sm leading-relaxed`}>
                    <div className="grid grid-cols-2 gap-3 text-[12px]">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">Coarsest</div>
                        <div className="font-semibold">{m.coarsest.name}</div>
                        <div className="font-mono text-xs">~{m.coarsest.mesh} mesh</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider opacity-70">Finest</div>
                        <div className="font-semibold">{m.finest.name}</div>
                        <div className="font-mono text-xs">~{m.finest.mesh} mesh</div>
                      </div>
                    </div>
                    {m.recommendation && (
                      <div className="mt-2.5 pt-2.5 border-t border-current/10 text-[12px]">
                        <b>Recommendation:</b> {m.recommendation}
                      </div>
                    )}
                    {m.flow_agent_detected && m.flow_agent_detected.length > 0 && (
                      <div className="mt-2 text-[11px] flex items-center gap-1.5 opacity-80">
                        <ShieldCheck size={12} weight="fill" />
                        Flow agent in formula: {m.flow_agent_detected.join(", ")}
                      </div>
                    )}
                  </div>
                  {m.unknown_count > 0 && (
                    <div className="text-[11px] text-stone-500 mt-2 italic">
                      {m.unknown_count} ingredient{m.unknown_count !== 1 ? "s" : ""} not in mesh database
                      ({m.unknown_ingredients.slice(0, 3).join(", ")}{m.unknown_ingredients.length > 3 ? "…" : ""}) — verify with supplier spec sheet.
                    </div>
                  )}
                  <div className="text-[10px] text-stone-400 mt-2">{m.citation}</div>
                </Card>
              );
            })()}

            {/* Application Calculator — DRY BLENDS ONLY.
                For wet products this calculator's "% on raw protein" math doesn't
                apply (sauces are diluted/poured by volume, broths are reconstituted
                by ratio, dressings are tossed). Wet/broth-specific calculators live
                inside <ApplicationCalculators /> below. */}
            {isDryProduct && (
            <Card className="p-5 border-stone-200" data-testid="application-calculator">
              <div className="flex items-center gap-2 mb-3">
                <Calculator size={18} className="text-brand-700" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Application Calculator</div>
                  <h3 className="font-semibold mt-0.5">How much seasoning do I use?</h3>
                </div>
              </div>
              {/* Category filter — hides irrelevant proteins for this seasoning */}
              <div className="mb-3">
                <LabelField label="What is this seasoning for?">
                  <Select value={intendedUseCategory} onValueChange={(v) => {
                    setIntendedUseCategory(v);
                    const allowed = PROTEIN_CATEGORIES[v] || [];
                    if (v !== "all" && allowed.length > 0 && !allowed.includes(appProtein)) {
                      setAppProtein(allowed[0]);
                    }
                  }}>
                    <SelectTrigger data-testid="intended-use-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meat_seafood">Meats & Seafood</SelectItem>
                      <SelectItem value="plant">Plant-based (vegetables, tofu, grains)</SelectItem>
                      <SelectItem value="snacks">Snacks (popcorn, chips)</SelectItem>
                      <SelectItem value="liquid">Soups & Broths</SelectItem>
                      <SelectItem value="all">All-purpose (show everything)</SelectItem>
                    </SelectContent>
                  </Select>
                </LabelField>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <LabelField label="Protein / Base">
                  <Select value={appProtein} onValueChange={setAppProtein}>
                    <SelectTrigger data-testid="app-protein-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {proteins
                        .filter((p) => intendedUseCategory === "all"
                          || (PROTEIN_CATEGORIES[intendedUseCategory] || []).includes(p.key))
                        .map((p) => (<SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </LabelField>
                <LabelField label="Weight (lb)">
                  <Input type="number" step="0.1" min="0.1" value={appWeightLb}
                    onChange={(e) => setAppWeightLb(e.target.value)}
                    data-testid="app-weight-input" />
                </LabelField>
                <LabelField label="Intensity">
                  <Select value={appIntensity} onValueChange={setAppIntensity}>
                    <SelectTrigger data-testid="app-intensity-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="heavy">Heavy</SelectItem>
                    </SelectContent>
                  </Select>
                </LabelField>
              </div>

              {appResult && (
                <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="app-result">
                  <Metric label="Light end" value={`${appResult.low_g}g`}
                    sub={`${appResult.low_oz}oz · ${appResult.low_pct}%`} testId="app-low" />
                  <Metric label="Recommended" value={`${appResult.mid_g}g`}
                    sub={`${appResult.mid_oz}oz · ${appResult.mid_pct}%`} highlight testId="app-mid" />
                  <Metric label="Heavy end" value={`${appResult.high_g}g`}
                    sub={`${appResult.high_oz}oz · ${appResult.high_pct}%`} testId="app-high" />
                </div>
              )}
              {appResult?.sodium_mid_mg_per_113g && (
                <div className="mt-3 p-3 rounded-md bg-brand-50/50 border border-brand-200 text-sm" data-testid="sodium-delivery">
                  <span className="text-xs uppercase tracking-wider text-brand-800 font-bold">Salt delivery</span>
                  <div className="mt-1 text-stone-700">
                    Per 4oz (113g) finished serving: <b>{appResult.sodium_mid_mg_per_113g}mg sodium</b> at recommended dose
                    <span className="text-xs text-stone-500"> ({appResult.sodium_low_mg_per_113g}–{appResult.sodium_high_mg_per_113g}mg range)</span>
                  </div>
                  {appResult.sodium_mid_mg_per_113g > 480 && (
                    <div className="text-xs text-amber-800 mt-1">
                      ⚠️ FDA "high sodium" threshold is 480mg/serving. Consider lighter intensity or reduce salt %.
                    </div>
                  )}
                </div>
              )}
              <div className="text-[11px] text-stone-500 mt-3 leading-relaxed">
                Industry rule of thumb: lighter for delicate proteins (fish, vegetables), heavier for crusts (chips, snacks).
                Adjust to brand & palate.
              </div>
            </Card>
            )}

            {/* Pet Toppers — pet-safety scan + Guaranteed-Analysis inputs */}
            {isPet && (
              <PetSafetyPanel
                pet={pet}
                moisturePct={spec.moisture_max_pct}
                setMoisturePct={(v) => setSpec({ ...spec, moisture_max_pct: v })}
                ashPct={ashPct}
                setAshPct={setAshPct}
              />
            )}

            {/* Labeling Intelligence — FDA rule scan with SOI validation (human food only) */}
            {!isPet && (
            <LabelingIntelligencePanel
              intel={calc?.labeling_intelligence}
              declaredSoi={declaredSoi}
              onSoiChange={setDeclaredSoi}
              productType={productType}
            />
            )}

            {/* Category-specific application calculators */}
            <Card className="p-5 border-stone-200" data-testid="application-calculators-card">
              <button
                type="button"
                onClick={() => setShowApplicationCalcs((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid="toggle-application-calculators"
              >
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Application Calculators</div>
                  <h3 className="font-semibold mt-0.5">
                    Reverse math: dose your blend into a finished product
                  </h3>
                  <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
                    {productType === "dry_blend"
                      ? "Snack and marinade application math: how much blend per pound of protein or per gallon of marinade."
                      : productType === "broth"
                      ? "Stock / ramen / bouillon math: how much concentrate to hit your finished sodium target after reduction."
                      : productType === "beverage"
                      ? "Brix mass-balance guidance for estimating concentrate and dilution water at a target batch volume."
                      : productType === "baked_goods"
                      ? "Baker's percentage, hydration, and pre-bake batch scaling with finished-yield reminders."
                      : productType === "dairy_plant"
                      ? "Category guidance is available in the formula, specification, and safety sections; a dedicated process module is not implied."
                      : productType === "pet_topper"
                      ? "Legacy water-activity, pH, salt, and hurdle review for pet-topper formulas."
                      : "Wet-sauce / dressing math + shelf-stability validator (a\u2090 + pH + salt hurdle technology)."}
                  </p>
                </div>
                <span className="text-stone-400 text-sm shrink-0 mt-1">
                  {showApplicationCalcs ? "▲ collapse" : "▼ expand"}
                </span>
              </button>
              {showApplicationCalcs && (
                <div className="mt-4 space-y-4">
                  <ApplicationCalculators productType={productType} />
                </div>
              )}
            </Card>

            {/* Acidified Foods QA Checklist — only shows for wet-product types (human food only) */}
            {!isPet && <AcidifiedFoodsChecklist productType={productType} />}

            {/* Safety Plan Worksheet — DRAFT hazard analysis for QA review (human food only) */}
            {!isPet && (
            <SafetyPlanPanel
              recipeId={id}
              productName={productName || name}
              productType={productType}
              ph={ph} setPh={setPh}
              waterActivity={waterActivity} setWaterActivity={setWaterActivity}
              thermalTempF={thermalTempF} setThermalTempF={setThermalTempF}
              thermalHoldMin={thermalHoldMin} setThermalHoldMin={setThermalHoldMin}
              acidified={acidified} setAcidified={setAcidified}
              killStep={killStep} setKillStep={setKillStep}
              distributionMode={distributionMode} setDistributionMode={setDistributionMode}
              safetyPlan={safetyPlan} setSafetyPlan={setSafetyPlan}
              onSave={save}
              isDemo={isDemo}
            />
            )}

            {/* R&D Notes */}
            <Card className="p-5 border-stone-200" data-testid="notes-card">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500">R&amp;D Notes</div>
              <h3 className="font-semibold mt-0.5 mb-3">Tasting & iteration log</h3>
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)}
                placeholder='Notes for this version — e.g., "Too sharp, dial back cayenne to 0.5%", "Needs salt", "Perfect on lamb chops"…'
                className="w-full min-h-[100px] p-3 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                data-testid="notes-textarea"
              />
              <div className="text-[11px] text-stone-500 mt-2">
                Saved with this version. Use <b>New version</b> button (top right) to fork & compare.
              </div>
            </Card>

            {/* Production Method (manufacturing directions — separate from R&D notes) */}
            <Card className="p-5 border-stone-200" data-testid="production-method-card">
              <button
                type="button"
                onClick={() => setShowProductionMethod((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid="toggle-production-method"
              >
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Production Method</div>
                  <h3 className="font-semibold mt-0.5">
                    Manufacturing directions
                    {productionMethod ? (
                      <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5">
                        loaded
                      </span>
                    ) : null}
                  </h3>
                  <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
                    Step-by-step batch directions. Prints on your spec sheet for the co-packer.
                  </p>
                </div>
                <span className="text-stone-400 text-sm shrink-0 mt-1">
                  {showProductionMethod ? "▲ collapse" : "▼ expand"}
                </span>
              </button>

              {showProductionMethod && (
                <div className="mt-4">
                  <textarea
                    value={productionMethod} onChange={(e) => setProductionMethod(e.target.value)}
                    placeholder={
                      productType === "dry_blend"
                        ? "Step-by-step blending procedure. Example:\n1. Plate oils onto salt/sugar carrier. Mix 5 min.\n2. Add remaining ingredients except large particulates and silicon dioxide. Mix 5 min.\n3. Add silicon dioxide last. Mix 2 min."
                        : productType === "wet_sauce"
                        ? "Step-by-step cook & fill procedure. Include hold temps, pH targets, fill temp, container & seal type."
                        : productType === "broth"
                        ? "Step-by-step simmer procedure. Include cook time, reduction targets, when to add aromatics, cooling protocol."
                        : "Step-by-step procedure. Include mix order, hydration time, pH check, fill & storage."
                    }
                    className="w-full min-h-[180px] p-3 rounded-md border border-stone-300 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    data-testid="production-method-textarea"
                  />
                  <div className="text-[11px] text-stone-500 mt-2">
                    Prints on the spec sheet. Co-packers and contract manufacturers will follow these exact directions.
                  </div>
                </div>
              )}
            </Card>

            {/* Lab Production Method — bench-scale, Fahrenheit, household equipment */}
            <Card className="p-5 border-stone-200" data-testid="lab-production-method-card">
              <button
                type="button"
                onClick={() => setShowLabProduction((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid="toggle-lab-production-method"
              >
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Lab Production Method</div>
                  <h3 className="font-semibold mt-0.5">
                    Bench-scale directions
                    {labProductionMethod ? (
                      <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5">
                        loaded
                      </span>
                    ) : null}
                  </h3>
                  <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
                    Stovetop / KitchenAid / kitchen scale equipment in Fahrenheit. The dry ingredients are referenced
                    as ONE pre-mix step — total weight comes from the formulation table above (no need to re-list
                    every spice).
                  </p>
                </div>
                <span className="text-stone-400 text-sm shrink-0 mt-1">
                  {showLabProduction ? "▲ collapse" : "▼ expand"}
                </span>
              </button>

              {showLabProduction && (
                <div className="mt-4">
                  <textarea
                    value={labProductionMethod}
                    onChange={(e) => setLabProductionMethod(e.target.value)}
                    placeholder={
                      productType === "dry_blend"
                        ? "Lab dry-blend steps using kitchen scale, mixing bowl, hand whisk, 30-mesh sieve. Reference the formulation table for the pre-blend totals."
                        : productType === "broth"
                        ? "Lab broth steps using a 6-qt stockpot on the burner. Heat to 195°F (use candy thermometer). Pre-blend the dry ingredients from the formulation table — add it in two stages."
                        : "Lab wet-sauce steps using a 4-qt saucepan + immersion blender + handheld pH meter. Pre-blend the dry ingredients from the formulation table as ONE pre-mix."
                    }
                    className="w-full min-h-[260px] p-3 rounded-md border border-stone-300 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    data-testid="lab-production-method-textarea"
                  />
                  {(() => {
                    // Compute "dry seasoning total" = sum of non-liquid grams
                    // (for the "collapsed" pre-mix figure shown in the lab method).
                    const drySum = rows.reduce((a, r) => {
                      if (r._is_liquid) return a;
                      return a + (Number(r.grams) || 0);
                    }, 0);
                    return (
                      <div className="mt-3 p-3 rounded-md bg-brand-50/50 border border-brand-200 text-[11px] text-brand-900 leading-relaxed">
                        <b>Dry-ingredient pre-blend total:</b>{" "}
                        <span className="font-mono">{drySum.toFixed(1)} g</span>{" "}
                        ({(drySum / 28.3495).toFixed(2)} oz · {(drySum / 453.592).toFixed(3)} lb).
                        <span className="ml-1">Combine all dry ingredients from the formulation table above
                        — the lab method references this single number, not a re-listing of every spice.</span>
                      </div>
                    );
                  })()}
                  <div className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                    <b>Editable.</b> Different from the commercial production method above — uses lab-scale
                    equipment (saucepan, KitchenAid, immersion blender, kitchen scale) and Fahrenheit
                    temperatures. Prints under the commercial method on your formulation sheet.
                  </div>
                </div>
              )}
            </Card>

            {/* Lab Bench Procedure — bench-side QA tests for this product type */}
            <Card className="p-5 border-stone-200" data-testid="lab-bench-procedure-card">
              <button
                type="button"
                onClick={() => setShowLabProcedure((v) => !v)}
                className="w-full flex items-start justify-between gap-3 text-left"
                data-testid="toggle-lab-bench-procedure"
              >
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Lab Bench Procedure</div>
                  <h3 className="font-semibold mt-0.5">
                    Bench-side QA tests
                    {labBenchProcedure ? (
                      <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-brand-700 bg-brand-50 border border-brand-200 rounded px-1.5 py-0.5">
                        loaded
                      </span>
                    ) : null}
                  </h3>
                  <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
                    pH · Brix · a<sub>w</sub> · salt · sensory · micro hold — embedded right under
                    your formula so you don't need a separate SOP file.
                  </p>
                </div>
                <span className="text-stone-400 text-sm shrink-0 mt-1">
                  {showLabProcedure ? "▲ collapse" : "▼ expand"}
                </span>
              </button>

              {showLabProcedure && (
                <div className="mt-4">
                  <textarea
                    value={labBenchProcedure}
                    onChange={(e) => setLabBenchProcedure(e.target.value)}
                    placeholder={
                      productType === "dry_blend"
                        ? "Lab QA steps for this dry blend — moisture, bulk density, mesh, salt %, sensory, micro release panel."
                        : productType === "broth"
                        ? "Lab QA steps for this broth — sodium target, pH, Brix, clarity, fill temp, micro release."
                        : "Lab QA steps for this wet product — pH (CRITICAL), Brix, salt %, water activity, fill temp, micro hold."
                    }
                    className="w-full min-h-[260px] p-3 rounded-md border border-stone-300 text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    data-testid="lab-bench-procedure-textarea"
                  />
                  <div className="text-[11px] text-stone-500 mt-2 leading-relaxed">
                    <b>Editable.</b> The starter loads a 21 CFR / AOAC reference procedure for this
                    product type. Customize for your product, your equipment, and your lab — these
                    steps print under the production method on your batch records.
                  </div>
                </div>
              )}
            </Card>

            {/* Co-Packer Spec Sheet */}
            <Card className="p-5 border-stone-200" data-testid="spec-sheet-card">
              <div className="flex items-start justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setShowSpecSheet((v) => !v)}
                  className="flex-1 text-left"
                  data-testid="toggle-spec-sheet"
                >
                  <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Co-Packer Spec Sheet</div>
                  <h3 className="font-semibold mt-0.5">
                    Product specification
                    <span className="ml-2 text-stone-400 text-sm font-normal">
                      {showSpecSheet ? "▲ collapse" : "▼ expand"}
                    </span>
                  </h3>
                  <p className="text-[11px] text-stone-500 mt-1 max-w-xl">
                    These fields populate your formal spec sheet. Defaults from Settings auto-fill new formulas.
                  </p>
                </button>
                <Button onClick={printSpecSheet} variant="outline" size="sm"
                  className="border-brand-700 text-brand-800 hover:bg-brand-50 shrink-0"
                  data-testid="print-spec-sheet-button">
                  <Files size={14} className="mr-1.5" /> Print spec sheet
                </Button>
              </div>

              {showSpecSheet && (
              <>
              <div className="space-y-4 mt-4">
                <SpecField label="Description" testId="spec-description">
                  <Input value={spec.description} onChange={(e) => setSpec({ ...spec, description: e.target.value })}
                    placeholder='e.g., A savory, herb-forward blend with garlic backbone, suitable for poultry rubs'/>
                </SpecField>

                <div className="grid sm:grid-cols-3 gap-3">
                  <SpecField label="Aroma" testId="spec-aroma">
                    <Input value={spec.aroma_profile} onChange={(e) => setSpec({ ...spec, aroma_profile: e.target.value })}
                      placeholder='Garlic-forward, herbaceous'/>
                  </SpecField>
                  <SpecField label="Flavor" testId="spec-flavor">
                    <Input value={spec.flavor_profile} onChange={(e) => setSpec({ ...spec, flavor_profile: e.target.value })}
                      placeholder='Savory, peppery finish'/>
                  </SpecField>
                  <SpecField label="Color" testId="spec-color">
                    <Input value={spec.color} onChange={(e) => setSpec({ ...spec, color: e.target.value })}
                      placeholder='Earth-toned, brown-green'/>
                  </SpecField>
                </div>

                {/* Flavor Architecture — sensory profile bars shown on the dashboard card */}
                <div data-testid="flavor-architecture-editor">
                  <div className="text-sm font-semibold text-stone-700 mb-0.5">Flavor Architecture</div>
                  <div className="text-xs text-stone-500 mb-2">
                    Rate 0–5. Shows as colored bars on the formulation card. Click a bar to set; click the same value again to clear.
                  </div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                    {[
                      { key: "front",   label: "Front",   hint: "up-front impact",    color: "bg-amber-400" },
                      { key: "body",    label: "Body",    hint: "mid-palate depth",   color: "bg-emerald-500" },
                      { key: "finish",  label: "Finish",  hint: "lingering length",   color: "bg-sky-500" },
                      { key: "thermal", label: "Thermal", hint: "blooms with heat",   color: "bg-rose-500" },
                    ].map((row) => (
                      <div key={row.key} className="flex items-center gap-2">
                        <span className="w-16 text-xs text-stone-600 font-medium" title={row.hint}>{row.label}</span>
                        <div className="flex gap-1.5 flex-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <button
                              key={i}
                              type="button"
                              aria-label={`${row.label} ${i} of 5`}
                              data-testid={`arch-${row.key}-${i}`}
                              onClick={() => setFlavorArch((fa) => ({ ...fa, [row.key]: fa[row.key] === i ? 0 : i }))}
                              className={`h-3 flex-1 max-w-[34px] rounded-full transition-colors ${
                                i <= flavorArch[row.key] ? row.color : "bg-stone-200 hover:bg-stone-300"
                              }`}
                            />
                          ))}
                        </div>
                        <span className="w-7 text-xs text-stone-400 text-right">{flavorArch[row.key]}/5</span>
                      </div>
                    ))}
                  </div>
                </div>

                {isDryProduct && (
                  <div className="grid sm:grid-cols-3 gap-3" data-testid="dry-spec-fields">
                    <SpecField label="Particle Size (mesh)" testId="spec-mesh">
                      <Input value={spec.mesh_size} onChange={(e) => setSpec({ ...spec, mesh_size: e.target.value })}
                        placeholder='80 mesh, 95% pass-through'/>
                    </SpecField>
                    <SpecField label="Bulk Density (g/mL)" testId="spec-density">
                      <Input type="number" step="0.01" value={spec.bulk_density_g_ml}
                        onChange={(e) => setSpec({ ...spec, bulk_density_g_ml: e.target.value })}
                        placeholder='0.55'/>
                    </SpecField>
                    <SpecField label="Moisture max (%)" testId="spec-moisture">
                      <Input type="number" step="0.1" value={spec.moisture_max_pct}
                        onChange={(e) => setSpec({ ...spec, moisture_max_pct: e.target.value })}
                        placeholder='8'/>
                    </SpecField>
                  </div>
                )}

                <SpecField label="Microbiological Spec" testId="spec-micro">
                  <textarea value={spec.micro_spec} onChange={(e) => setSpec({ ...spec, micro_spec: e.target.value })}
                    placeholder='TPC <100,000 CFU/g · Yeast & Mold <1,000 CFU/g · E. coli negative · Salmonella negative · Listeria negative'
                    className="w-full min-h-[60px] p-2 rounded-md border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"/>
                </SpecField>

                <div className="grid sm:grid-cols-3 gap-3">
                  <SpecField label="Shelf Life (months)" testId="spec-shelf-life">
                    <Input type="number" step="1" value={spec.shelf_life_months}
                      onChange={(e) => setSpec({ ...spec, shelf_life_months: e.target.value })}
                      placeholder='12'/>
                    <div className="text-[10px] text-stone-500 mt-1 leading-snug">
                      Default: 12 mo. Override: 24 mo (pure salt/low-aromatic) · 9 mo (real-oil flavors)
                    </div>
                  </SpecField>
                  <SpecField label="Storage" testId="spec-storage">
                    <Input value={spec.storage_conditions} onChange={(e) => setSpec({ ...spec, storage_conditions: e.target.value })}
                      placeholder='Cool, dry · <75°F · <60% RH'/>
                  </SpecField>
                  <SpecField label="Packaging" testId="spec-packaging">
                    <Input value={spec.packaging} onChange={(e) => setSpec({ ...spec, packaging: e.target.value })}
                      placeholder='25 lb double-poly bag'/>
                  </SpecField>
                </div>

                <SpecField label="Application Notes" testId="spec-application">
                  <Input value={spec.application_notes} onChange={(e) => setSpec({ ...spec, application_notes: e.target.value })}
                    placeholder='2.5–3.5% on chicken; 4–6% on snacks'/>
                </SpecField>
              </div>

              <div className="mt-4 p-3 rounded-md bg-brand-50/50 border border-brand-200 text-[11px] text-brand-900 leading-relaxed">
                <b>Salt tolerance auto-calculated</b> from your formula's salt %. ASTA-style bands:
                0–10% → ±0.5% · 10–20% → ±1.0% · 20–30% → ±1.5% · 30%+ → ±2.0%.
                Visible on the printed spec sheet.
              </div>
              </>
              )}
            </Card>

            {rows.length > 0 && (
              <Card className="p-5 border-stone-200" data-testid="cost-card">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Cost & Pricing</div>
                    <h3 className="font-semibold mt-0.5">What does it cost — and what do I sell it for?</h3>
                  </div>
                  {costs.missing > 0 && (
                    <div className="text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded-md px-2 py-1"
                      data-testid="missing-prices-warning">
                      {costs.missing} ingredient{costs.missing !== 1 ? "s" : ""} missing $/lb — set on Ingredients page
                    </div>
                  )}
                </div>

                {/* SECTION 0 — Pack-out (defines what a "unit" is) */}
                <div className="rounded-lg border border-brand-300 bg-brand-50/30 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-200 text-brand-900">0</span>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-900 font-semibold">What are you selling?</div>
                    <span className="text-[10px] text-brand-800/70">defines one "unit" — a jar, pouch, bottle, etc.</span>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <LabelField label="Pack format">
                      <Input value={unitLabel}
                        onChange={(e) => setUnitLabel(e.target.value)}
                        placeholder="4 oz jar" data-testid="unit-label-input" />
                    </LabelField>
                    <LabelField label="Net weight per pack (grams)">
                      <Input type="number" step="0.1" min="0" value={unitSizeG}
                        onChange={(e) => { setUnitSizeG(e.target.value); setNetWeight(e.target.value ? `${e.target.value} g` : ""); }}
                        placeholder="113.4 (4 oz)" data-testid="unit-size-g-input" />
                    </LabelField>
                    <LabelField label={costs.derivedUnits > 0 ? "Units from this batch (auto)" : "Units from this batch"}>
                      {costs.derivedUnits > 0 ? (
                        <div className="h-9 flex items-center font-mono text-sm text-stone-700">
                          {costs.derivedUnits} {unitLabel ? unitLabel + (costs.derivedUnits === 1 ? "" : "s") : (costs.derivedUnits === 1 ? "unit" : "units")}
                          <span className="ml-2 text-[10px] text-stone-500">
                            ({totalG.toFixed(0)}g ÷ {Number(unitSizeG).toFixed(1)}g)
                          </span>
                        </div>
                      ) : (
                        <Input type="number" step="1" min="1" value={unitsPerBatch}
                          onChange={(e) => setUnitsPerBatch(e.target.value)}
                          placeholder="10" data-testid="units-per-batch-input" />
                      )}
                    </LabelField>
                  </div>
                  <div className="text-[11px] text-brand-900/70 mt-2">
                    Common pack sizes (g): <b>4 oz = 113.4</b> · 3.5 oz = 99 · 8 oz = 226.8 · 12 oz = 340 · 16 oz = 453.6
                  </div>
                </div>

                {/* SECTION 1 — Ingredient Cost (auto from $/lb) */}
                <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50/40 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-stone-200 text-stone-700">1</span>
                    <div className="text-xs uppercase tracking-[0.18em] text-stone-600 font-semibold">Ingredient Cost</div>
                    <span className="text-[10px] text-stone-400">auto · raw materials only · from $/lb on Ingredients page</span>
                  </div>
                  <div className="grid sm:grid-cols-4 gap-3">
                    <Metric label="Whole batch" value={`$${costs.batchCost.toFixed(2)}`} sub={`for ${totalG.toFixed(1)}g of blend`} testId="metric-batch-cost" />
                    <Metric label="Per pound" value={`$${costs.costPerLb.toFixed(2)}`} sub="bulk / co-packer basis" testId="metric-cost-lb" />
                    <Metric label="Per serving" value={`$${costs.costPerServing.toFixed(3)}`} sub={`per ${servingSize}g`} testId="metric-cost-serving" />
                    <Metric label={`Per ${unitLabel || "unit"}`} value={`$${costs.ingredientCogsPerUnit.toFixed(2)}`} sub={`÷ ${costs.units} ${unitLabel ? unitLabel + (costs.units === 1 ? "" : "s") : (costs.units === 1 ? "unit" : "units")}`} testId="metric-ing-per-unit" />
                  </div>
                </div>

                {/* SECTION 2 — Production Cost (user-entered overhead) */}
                <div className="mt-4 rounded-lg border border-stone-200 bg-white p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-stone-200 text-stone-700">2</span>
                    <div className="text-xs uppercase tracking-[0.18em] text-stone-600 font-semibold">
                      Production Cost <span className="normal-case font-normal text-stone-500">(US dollars per {unitLabel || "unit"})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPackagingPerUnit(INDUSTRY_DEFAULT_COSTS.packaging_per_unit);
                        setLaborPerUnit(INDUSTRY_DEFAULT_COSTS.labor_per_unit);
                        setCopackerPerUnit(INDUSTRY_DEFAULT_COSTS.copacker_per_unit);
                        setOverheadPerUnit(INDUSTRY_DEFAULT_COSTS.overhead_per_unit);
                        toast.success("Loaded industry-average costs — adjust as needed");
                      }}
                      className="ml-auto text-[11px] px-2 py-1 rounded border border-brand-200 bg-brand-50 text-brand-800 hover:bg-brand-100 transition"
                      data-testid="use-industry-defaults-btn"
                    >
                      Use industry averages
                    </button>
                  </div>
                  <div className="text-[11px] text-stone-600 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                    Enter <b>US dollars</b> (not %). Click <b>"Use industry averages"</b> for typical 4 oz jar / hand-fill costs (~$1.55 per unit total). Adjust each field for your actual numbers.
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <LabelField label={`Packaging $ per ${unitLabel || "unit"} — empty bottle + cap + label + sticker`}>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                        <Input type="number" step="0.01" min="0" value={packagingPerUnit}
                          onChange={(e) => setPackagingPerUnit(e.target.value)}
                          placeholder={`industry avg $${INDUSTRY_DEFAULT_COSTS.packaging_per_unit.toFixed(2)}`} className="pl-6"
                          data-testid="packaging-per-unit-input" />
                      </div>
                    </LabelField>
                    <LabelField label={`Labor $ per ${unitLabel || "unit"} — your time to fill / cap / label`}>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                        <Input type="number" step="0.01" min="0" value={laborPerUnit}
                          onChange={(e) => setLaborPerUnit(e.target.value)}
                          placeholder={`industry avg $${INDUSTRY_DEFAULT_COSTS.labor_per_unit.toFixed(2)} ($18/hr × 30 jars/hr)`} className="pl-6"
                          data-testid="labor-per-unit-input" />
                      </div>
                    </LabelField>
                    <LabelField label={`Co-packer fee $ per ${unitLabel || "unit"} — what they charge per finished unit`}>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                        <Input type="number" step="0.01" min="0" value={copackerPerUnit}
                          onChange={(e) => setCopackerPerUnit(e.target.value)}
                          placeholder="0.00 (typical co-packer $0.50–1.50)" className="pl-6"
                          data-testid="copacker-per-unit-input" />
                      </div>
                    </LabelField>
                    <LabelField label={`Other overhead $ per ${unitLabel || "unit"} — kitchen rent, utilities, misc`}>
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                        <Input type="number" step="0.01" min="0" value={overheadPerUnit}
                          onChange={(e) => setOverheadPerUnit(e.target.value)}
                          placeholder={`industry avg $${INDUSTRY_DEFAULT_COSTS.overhead_per_unit.toFixed(2)}`} className="pl-6"
                          data-testid="overhead-per-unit-input" />
                      </div>
                    </LabelField>
                  </div>
                  <div className="mt-4 rounded-md bg-stone-900 text-white px-4 py-3 flex items-center justify-between"
                    data-testid="metric-loaded-cogs">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-400">Total cost to make ONE {unitLabel || "unit"}</div>
                      <div className="text-[10px] text-stone-500 mt-0.5">ingredients ${costs.ingredientCogsPerUnit.toFixed(2)} + packaging ${costs.packagingPerUnit.toFixed(2)} + labor ${costs.laborPerUnit.toFixed(2)} + co-packer ${costs.copackerPerUnit.toFixed(2)} + overhead ${costs.overheadPerUnit.toFixed(2)}</div>
                    </div>
                    <div className="font-mono text-2xl">${costs.fullyLoadedCogsPerUnit.toFixed(2)}</div>
                  </div>
                </div>

                {/* SECTION 3 — Pricing & Margin */}
                <div className="mt-4 rounded-lg border-2 border-brand-300 bg-brand-50/40 p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-brand-200 text-brand-900">3</span>
                    <div className="text-xs uppercase tracking-[0.18em] text-brand-900 font-semibold">What do I sell it for?</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4 mb-4">
                    <LabelField label="Markup % (CPG standard: 200–400% for retail, 100% for wholesale)">
                      <div className="relative">
                        <Input type="number" step="10" min="0" value={markupPct}
                          onChange={(e) => setMarkupPct(e.target.value)}
                          placeholder="300" className="pr-8"
                          disabled={Number(retailOverride) > 0}
                          data-testid="markup-input" />
                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">%</span>
                      </div>
                    </LabelField>
                    <LabelField label="…or set retail price directly (overrides markup)">
                      <div className="relative">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
                        <Input type="number" step="0.01" min="0" value={retailOverride}
                          onChange={(e) => setRetailOverride(e.target.value)}
                          placeholder="leave blank to use markup" className="pl-6"
                          data-testid="retail-override-input" />
                      </div>
                    </LabelField>
                  </div>

                  {/* THE BIG ANSWER */}
                  <div className="rounded-lg bg-brand-700 text-white px-5 py-5 mb-3" data-testid="big-answer">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-brand-200 mb-1">Sell each {unitLabel || "unit"} for</div>
                    <div className="font-mono text-5xl tracking-tight">${costs.suggestedRetailPerUnit.toFixed(2)}</div>
                    <div className="text-sm text-brand-100 mt-2">
                      You keep <b className="text-white">${costs.grossMarginPerUnit.toFixed(2)}</b> per {unitLabel || "unit"}
                      ({costs.grossMarginPct.toFixed(0)}% margin) ·
                      Profit per batch: <b className="text-white">${costs.profitPerBatch.toFixed(2)}</b> across {costs.units} {unitLabel ? unitLabel + (costs.units === 1 ? "" : "s") : (costs.units === 1 ? "unit" : "units")}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-3 text-[11px] text-stone-700 bg-white/70 rounded p-3 border border-brand-100">
                    <div><b>Markup</b><br/><span className="text-stone-500">(Retail − Cost) ÷ Cost = how much you mark UP from your cost</span></div>
                    <div><b>Margin</b><br/><span className="text-stone-500">(Retail − Cost) ÷ Retail = % of each sale that's profit</span></div>
                    <div><b>Healthy CPG benchmarks</b><br/><span className="text-stone-500">Retail: 3–5× cost · Wholesale: 2× cost · Margin: 50–70%</span></div>
                  </div>
                </div>
              </Card>
            )}

            {calc?.flavor_radar && (
              <Card className="p-5 border-stone-200" data-testid="flavor-radar-builder">
                <div className="text-xs text-stone-500 mb-2">
                  Auto-computed from your ingredients — updates every time the formula changes.
                </div>
                <FlavorRadar radar={calc.flavor_radar} size={170} />
              </Card>
            )}

            {!isPet && calc?.claims && calc.claims.length > 0 && <ClaimsPanel claims={calc.claims} />}

            <AIAssistant recipeContext={calc ? {
              product: productName || name,
              serving_size_g: servingSize,
              per_serving: calc.nutrition?.per_serving,
              ingredient_statement: calc.statement.statement,
              contains: calc.statement.contains_line,
            } : null} />
          </div>

          {/* RIGHT: Label preview */}
          <div className="lg:sticky lg:top-20 h-fit">
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">
              {isPet ? "Live AAFCO pet preview" : "Live FDA preview"}
            </div>
            {calc?.nutrition_error ? (
              <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="nutrition-pending">
                <b>Nutrition Facts unavailable</b>
                <p className="mt-2">{calc.nutrition_error}</p>
                <p className="mt-2">You can save this formula and use its ingredient statement. Nutrition labels and nutrient claims require complete supplier data.</p>
              </div>
            ) : calc && isPet ? (
              <div className="print-label" data-export-section data-testid="nutrition-section">
                <SectionExport />
                <GuaranteedAnalysisLabel
                  pet={pet}
                  brandName={bp.brand_name}
                  productName={productName}
                  netWeight={displayNetWeight}
                  businessProfile={bp}
                />
                <div className="mt-2 text-[8px] text-stone-600 max-w-[320px] leading-tight">
                  {pet?.disclaimer || "Guaranteed Analysis values are calculated estimates, not a lab proximate analysis. Confirm with laboratory analysis and a veterinary / AAFCO review before sale."}
                </div>
              </div>
            ) : calc ? (
              <div className="print-label" data-export-section data-testid="nutrition-section">
                <SectionExport />
                {/* Brand + product header — prints on PDF */}
                {(bp.brand_name || productName) && (
                  <div className="font-fda text-black bg-white border-[3px] border-black border-b-0 px-2 pt-2 pb-1 w-full max-w-[320px] text-center">
                    {bp.logo_data && <img src={bp.logo_data} alt="" className="max-h-[60px] max-w-[120px] object-contain mx-auto mb-1" />}
                    {bp.brand_name && <div className="text-[14px] font-black tracking-tight">{bp.brand_name.toUpperCase()}</div>}
                    {productName && <div className="text-[12px] font-bold">{productName}</div>}
                    <div className="text-[10px] mt-0.5">NET WT {displayNetWeight}</div>
                  </div>
                )}
                <NutritionLabel calc={calc} isLiquid={isLiquidProduct} density={avgDensity}
                  servingCountDisplay={servingCountDisplay} servingSizeDisplay={servingSizeDisplay} />
                {calc.statement?.statement && (
                  <div className="font-fda text-[9px] text-black bg-white border-x-[3px] border-b-[3px] border-black px-2 py-2 w-full max-w-[320px] leading-tight">
                    <div className="font-bold">{calc.statement.statement}</div>
                    {calc.statement.contains_line && <div className="font-bold mt-1">{calc.statement.contains_line}</div>}
                    {(bp.legal_entity || bp.address_line1) && (
                      <div className="mt-2 pt-2 border-t border-black text-[8.5px]">
                        {bp.responsible_party_prefix || "Distributed by"} {bp.legal_entity}
                        {(bp.address_line1 || bp.city) && <br />}
                        {bp.address_line1}{bp.address_line2 ? `, ${bp.address_line2}` : ""}
                        {(bp.city || bp.state) && <br />}
                        {bp.city}{bp.city && bp.state ? ", " : ""}{bp.state} {bp.zip_code}
                        {bp.website && <><br />{bp.website}</>}
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-2 text-[8px] text-stone-600 max-w-[320px] leading-tight">
                  Nutrition values calculated from ingredient database values using FDA rounding rules
                  (21 CFR 101.9). Final label review is the responsibility of the manufacturer.
                </div>
                {!isDemo && (
                  <div className="mt-3 w-full max-w-[320px] space-y-2">
                    <Select value={labelPreset} onValueChange={setLabelPreset}>
                      <SelectTrigger className="h-9 text-xs" data-testid="label-size-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LABEL_PRESETS).map(([k, v]) => (
                          <SelectItem key={k} value={k} className="text-xs">{v.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={labelPurpose} onValueChange={setLabelPurpose}>
                      <SelectTrigger className="h-9 text-xs" data-testid="label-purpose-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(LABEL_PURPOSES).map(([k, v]) => (
                          <SelectItem key={k} value={k} className="text-xs">
                            {v.icon} {v.name}{v.stamp ? " (stamped)" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <LabelReadiness calc={calc} bp={bp} netWeight={displayNetWeight || netWeight} productName={productName} recipeName={name} mode={mode} ph={ph} />
                    <Button variant="outline" onClick={() => print(labelPreset, labelPurpose)} data-testid="print-label-preview-button"
                      className="w-full border-brand-300 text-brand-800 hover:bg-brand-50"
                      title="Opens a print-ready page at the selected label size. Choose 'Save as PDF' in the print dialog to get a file for your label printer. Bottle/jar sizes use the FDA linear small-package format.">
                      <Printer size={16} className="mr-2" />
                      Generate Label
                    </Button>
                    {LABEL_PRESETS[labelPreset]?.wrap && (
                      <div className="text-[8px] text-stone-500 leading-tight">
                        Bottle/jar wraps print at exact size using the FDA linear small-package
                        nutrition format (21 CFR 101.9(j)(13)) — standard for spice containers.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-md border-2 border-dashed border-stone-300 p-10 text-center text-stone-400 text-sm">
                Add ingredients to preview your label.
              </div>
            )}
            {productName && (
              <div className="mt-2 text-[11px] text-stone-500 max-w-[320px]">
                Product: <b>{productName}</b>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ingredient Intelligence drawer (modal) */}
      {intelDrawer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => setIntelDrawer(null)}
          data-testid="intel-drawer">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-stone-200 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-700 font-bold">
                  Ingredient Intelligence
                </div>
                <h2 className="text-2xl font-bold tracking-tight" data-testid="intel-name">
                  {intelDrawer.ingredient?.name || intelDrawer.name}
                </h2>
                <div className="text-[11px] text-stone-500 mt-1">
                  Declared as: "{intelDrawer.ingredient?.declaration}"
                </div>
              </div>
              <button onClick={() => setIntelDrawer(null)}
                className="h-8 w-8 rounded-md hover:bg-stone-100 grid place-items-center text-stone-500"
                data-testid="intel-close">✕</button>
            </div>
            <div className="p-5 space-y-4">
              {intelDrawer.curated && intelDrawer.intel ? (
                <>
                  <IntelRow label="Flavor profile">{intelDrawer.intel.flavor_profile}</IntelRow>
                  <IntelRow label="Typical use">{intelDrawer.intel.typical_pct}</IntelRow>
                  <IntelRow label="Upper limit ⚠️">{intelDrawer.intel.upper_limit}</IntelRow>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1.5">Pairs well with</div>
                    <div className="flex flex-wrap gap-1">
                      {intelDrawer.intel.pairs_with.map((p) => (
                        <span key={p} className="text-xs px-2 py-1 rounded-full bg-brand-50 text-brand-800 border border-brand-200">
                          {p}
                        </span>
                      ))}
                    </div>
                  </div>
                  <IntelRow label="Notes">{intelDrawer.intel.notes}</IntelRow>
                </>
              ) : (
                <div className="text-stone-500 text-sm">
                  Detailed intelligence not yet curated for this ingredient.
                  Industry usage limits still apply (check the Formulation flags panel).
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
    </SectionExportContext.Provider>
  );
}

function IntelRow({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-1">{label}</div>
      <div className="text-sm text-stone-800 leading-relaxed">{children}</div>
    </div>
  );
}

function SpecField({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function LabelField({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-stone-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Metric({ label, value, sub, highlight, testId }) {
  return (
    <div
      className={`rounded-md border px-3 py-2 ${highlight ? "bg-brand-50 border-brand-300" : "bg-stone-50 border-stone-200"}`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wider text-stone-500">{label}</div>
      <div className={`font-mono text-lg font-semibold ${highlight ? "text-brand-800" : "text-stone-800"}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-stone-500 mt-0.5">{sub}</div>}
    </div>
  );
}
